import { useTenant } from '../Context/TenantContext';
// src/components/TeamRoles.jsx - Complete replica of IndividualRoles with team chat
import React, { useEffect, useState, useRef, useMemo } from"react";
import { useNavigate } from"react-router-dom";
import {
 PlayCircleIcon, CheckCircleIcon, TrophyIcon, ClipboardDocumentListIcon,
 LockClosedIcon, BookOpenIcon, XMarkIcon, DocumentTextIcon,
 ChatBubbleLeftRightIcon, ArrowPathIcon, ShieldCheckIcon,
 ExclamationTriangleIcon, PhotoIcon, VideoCameraIcon, UserGroupIcon, Bars3Icon,
 SpeakerWaveIcon, PaperClipIcon, PaperAirplaneIcon,
 ArrowTopRightOnSquareIcon, ArrowDownTrayIcon,
 CheckCircleIcon as CheckCircleIconSolid
} from"@heroicons/react/24/outline";
import SubscriptionStatus from"./SubscriptionStatus";
import VoiceAssistant from './NewVoiceAssistant';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';
import SimplePracticeCodingButton from './SimplePracticeCodingButton';
import TeamScoreWidget from './TeamScoreWidget';
import ThemeToggle from './ThemeToggle';
import { useTaskCompletion } from '../hooks/useTaskCompletion';
import API_BASE_URL from '../Config/config';
import { hasFullAccess, redirectToPaymentIfNeeded } from '../utils/paymentStatus';
import AchievementSystem from "./AchievementSystem";
import AIMentorController from "./AIMentorController";
import { checkLLMTrialStatus as checkLLMTrialStatusUtil } from '../utils/llmTrialManager';
import VisualizationPreview from"./shared/VisualizationPreview";

/* -------------------------------------------------------------------------- */
/* utils */
/* -------------------------------------------------------------------------- */
const GitHubIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
  </svg>
);
const isDark = false;
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const slugify = (s ="") =>
 s.toString().trim().toLowerCase()
 .replace(/&/g," and")
 .replace(/\+/g,"")
 .replace(/[^a-z0-9]+/g,"-")
 .replace(/(^-|-$)+/g,"");

// Safely convert explanation values to strings (handles objects, arrays, etc.)
const safeToString = (value) => {
 if (value == null) return '';
 if (typeof value === 'string') return value;
 if (typeof value === 'number' || typeof value === 'boolean') return String(value);
 if (Array.isArray(value)) return value.join('. ');
 if (typeof value === 'object') {
 // If it's an explanation object, format it properly
 if (value.topicOverview || value.keyConcepts || value.subtopicExplanations) {
 let formatted = '';
 if (value.topicOverview) formatted += safeToString(value.topicOverview) + '\n\n';
 if (Array.isArray(value.keyConcepts) && value.keyConcepts.length > 0) {
 formatted += 'Key Concepts:\n';
 value.keyConcepts.forEach((concept, idx) => {
 formatted += `${idx + 1}. ${safeToString(concept)}\n`;
 });
 formatted += '\n';
 }
 if (value.subtopicExplanations && typeof value.subtopicExplanations === 'object') {
 Object.entries(value.subtopicExplanations).forEach(([key, val]) => {
 formatted += `${key}:\n${safeToString(val)}\n\n`;
 });
 }
 if (value.practicalExamples) formatted += `Practical Examples:\n${safeToString(value.practicalExamples)}\n\n`;
 if (value.commonMistakes) formatted += `Common Mistakes:\n${safeToString(value.commonMistakes)}\n\n`;
 if (value.bestPractices) formatted += `Best Practices:\n${safeToString(value.bestPractices)}\n`;
 return formatted.trim() || JSON.stringify(value);
 }
 try {
 return JSON.stringify(value);
 } catch {
 return String(value);
 }
 }
 return String(value);
};

// Convert CloudFront/S3 URLs to proxy URLs to avoid CORS issues
const getProxiedVideoUrl = (url) => {
 if (!url) return url;
 
 // Check if URL is from CloudFront or S3
 const cloudfrontPattern = /https?:\/\/d1atm4dw3zgpab\.cloudfront\.net\//;
 const s3Pattern = /https?:\/\/.*\.s3\.(.*\.)?amazonaws\.com\//;
 
 if (cloudfrontPattern.test(url) || s3Pattern.test(url)) {
 // Convert to proxy URL
 return `${API_BASE_URL}/api/video/proxy?url=${encodeURIComponent(url)}`;
 }
 
 // Not a CloudFront/S3 URL, return as is
 return url;
};

/* -------------------------------------------------------------------------- */
/* local storage */
/* -------------------------------------------------------------------------- */
const pKey = (userId, tag) => `progress_${userId}_${slugify(tag)}`;
const readProgress = (userId, tag) => {
 try {
 const raw = localStorage.getItem(pKey(userId, tag));
 if (!raw) return { completedTaskIds: [], reviewStateById: {} };
 const parsed = JSON.parse(raw);
 if (Array.isArray(parsed)) return { completedTaskIds: parsed, reviewStateById: parsed.reviewStateById || {} };
 return { completedTaskIds: parsed.completedTaskIds || [], reviewStateById: parsed.reviewStateById || {} };
 } catch { return { completedTaskIds: [], reviewStateById: {} }; }
};
const writeProgress = (userId, tag, obj) => {
  try {
    localStorage.setItem(pKey(userId, tag), JSON.stringify(obj));
    // Dispatch event to notify other components (like CertificatePage) that progress has changed
    window.dispatchEvent(new CustomEvent('skillgenProgressUpdated', { detail: { userId, tag } }));
  } catch {}
};

const mKey = (userId, tag) => `mentor_${userId}_${slugify(tag)}`;
const readChat = (userId, tag) => { try { return JSON.parse(localStorage.getItem(mKey(userId, tag)) ||"[]"); } catch { return []; } };
const writeChat = (userId, tag, msgs) => { try { localStorage.setItem(mKey(userId, tag), JSON.stringify(msgs)); } catch {} };

/* -------------------------------------------------------------------------- */
/* Progress Sync Function */
/* -------------------------------------------------------------------------- */
const syncProgressToBackend = async (userId, bunchName, completedTaskIds, totalTasks) => {
 try {
 const progressPercentage = totalTasks > 0 ? Math.round((completedTaskIds.length / totalTasks) * 100) : 0;
 
 console.log('🔄 Syncing team progress to backend:', {
 userId,
 bunchName,
 completedTaskIds,
 progressPercentage,
 totalTasks,
 url: `${API_BASE_URL}/api/certificate/sync-progress`
 });
 
 const response = await fetch(`${API_BASE_URL}/api/certificate/sync-progress`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({
 userId,
 bunchName,
 completedTaskIds,
 progressPercentage,
 totalTasks
 })
 });
 
 const data = await response.json();
 console.log('✅ Team progress synced:', data);
 } catch (error) {
 console.error('❌ Failed to sync team progress:', error);
 }
};

/* -------------------------------------------------------------------------- */
/* backend calls */
/* -------------------------------------------------------------------------- */
async function askMentorAPI({ userId, technology, experienceLevel, task, text, imgFile, history = [] }) {
 const url = `${API_BASE_URL}/api/bootcamp/roles/mentor`;
 try {
 const fd = new FormData();
 fd.append("userId", userId);
 fd.append("roleName", technology);
 fd.append("experienceLevel", experienceLevel);
 fd.append("taskId", task?.id ||"");
 fd.append("taskTitle", task?.title || task?.topic ||"");
 fd.append("context", JSON.stringify({
 expectedOutput: task?.expectedOutput ||"",
 subtopics: task?.subtopics || [],
 description: task?.description ||"",
 }));
 fd.append("message", text ||"");
 fd.append("history", JSON.stringify(history.slice(-10).map(m => ({ role: m.role, text: m.text }))));
 if (imgFile) fd.append("image", imgFile);
 
 const res = await fetch(url, { method:"POST", body: fd });
 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data;
 } catch (err) {
 const header = `Demo mentor (API unreachable):\n\n`;
 const tail = `\n\nConfigure /api/bootcamp/tech/mentor for real mentoring.`;
 return { ok: true, answer: header + (text ||"Share your exact error.") + tail, mock: true, error: String(err?.message || err) };
 }
}

const API = API_BASE_URL;

async function initializeLLMTrial(userId) {
 const trialKey = `llm_trial_${userId}`;
 const existingTrial = localStorage.getItem(trialKey);
 
 if (!existingTrial) {
 const trialStart = new Date();
 const trialEnd = new Date(trialStart.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days
 const trial = {
 startDate: trialStart.toISOString(),
 endDate: trialEnd.toISOString(),
 used: false
 };
 localStorage.setItem(trialKey, JSON.stringify(trial));
 return trial;
 }
 
 return JSON.parse(existingTrial);
};

/* -------------------------------------------------------------------------- */
/* UI Components */
/* -------------------------------------------------------------------------- */
const TabButton = ({ active, onClick, children }) => (
  <button 
    onClick={onClick} 
    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${
      active 
        ? "bg-primary text-white border-primary shadow-lg shadow-primary/25" 
        : "bg-white text-muted border-gray-200 hover:border-primary/50 hover:text-primary"
    }`}
  >
    {children}
  </button>
);
const Button = ({ children, onClick, className = "", variant = "primary", ...props }) => {
  const baseStyles = "px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/25",
    outline: "border-2 border-primary text-primary hover:bg-primary/5",
    secondary: "bg-secondary/10 text-primary hover:bg-secondary/20"
  };
  return (
    <button onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};
const Field = ({ label, children }) => (<div><div className="text-sm text-muted">{label}</div>{children}</div>);
const Info = ({ label, children }) => (<div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-3"><div className="text-xs text-muted mb-1">{label}</div><p className="text-sm text-gray-700 whitespace-pre-line">{children}</p></div>);

const ActionCard = ({ title, subtitle, icon, onClick, disabled = false }) => (
 <button
 onClick={onClick}
 disabled={disabled}
 className="group p-4 rounded-xl border border-gray-300 bg-white/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-secondary/50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
 >
 <div className="flex items-start gap-3">
 <div className="p-2 rounded-lg bg-gray-200/50 group-hover:bg-primary/10 transition-colors">
 {icon}
 </div>
 <div className="flex-1 min-w-0">
 <h4 className="font-semibold text-gray-900 text-sm group-hover:text-primary transition-colors">
 {title}
 </h4>
 <p className="text-xs text-gray-600 mt-1 line-clamp-2">
 {subtitle}
 </p>
 </div>
 </div>
 </button>
);

const ProgressRing = ({ percent, size = 80, strokeWidth = 6 }) => {
 const radius = (size - strokeWidth) / 2;
 const circumference = radius * 2 * Math.PI;
 const strokeDasharray = circumference;
 const strokeDashoffset = circumference - (percent / 100) * circumference;

 return (
 <div className="relative flex items-center justify-center">
 <svg width={size} height={size} className="transform -rotate-90">
 <circle
 cx={size / 2}
 cy={size / 2}
 r={radius}
 stroke="rgb(55 65 81)"
 strokeWidth={strokeWidth}
 fill="transparent"
 />
 <circle
 cx={size / 2}
 cy={size / 2}
 r={radius}
 stroke="rgb(251 191 36)"
 strokeWidth={strokeWidth}
 fill="transparent"
 strokeDasharray={strokeDasharray}
 strokeDashoffset={strokeDashoffset}
 strokeLinecap="round"
 className="transition-all duration-300 ease-in-out"
 />
 </svg>
 <div className="absolute inset-0 flex items-center justify-center">
 <span className="text-primary font-bold text-lg">{percent}%</span>
 </div>
 </div>
 );
};

/* -------------------------------------------------------------------------- */
/* backend calls - Team Roles (using teamroles JSON data) */
/* -------------------------------------------------------------------------- */
async function fetchUserContext(userId, teamId) {
 // Try to get user from individual roles first
 try {
 const res = await fetch(`${API}/api/individual-roles/user-data/${userId}`);
 const data = await res.json();
 if (res.ok && data.success) {
 const userData = data.data;
 return { 
 user: userData, 
 enrollment: { 
 track: 'team', 
 bunchName: userData.role, 
 experienceLevel: userData.experience_level,
 githubRepoUrl: userData.github_repo_url,
 invitationStatus: userData.invitation_status,
 enrollmentId: userData.enrollment_id
 } 
 };
 }
 } catch (err) {
 console.log('Could not fetch from individual roles, trying team data...');
 }

 // If not found in individual roles, try to get from team data
 try {
 const teamRes = await fetch(`${API}/api/teams/${teamId}/members`);
 const teamData = await teamRes.json();
 if (teamData.success && teamData.members) {
 const userData = teamData.members.find(member => member.user_id === userId);
 if (userData) {
 return { 
 user: { 
 user_id: userId, 
 name: userData.name, 
 role: 'Software Engineer',
 experience_level: userData.experience_level || 'Intermediate'
 }, 
 enrollment: { 
 track: 'team', 
 bunchName: 'Software Engineer', 
 experienceLevel: userData.experience_level || 'Intermediate' 
 } 
 };
 }
 }
 } catch (err) {
 console.log('Could not fetch from team data...');
 }

 throw new Error('User not found in individual roles or team data');
}

async function loadTeamTasks(teamId, userId) {
  let isPaid = false;
  let allTasks = [];
  try {
    // Check if user is paid
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || 
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';
    
    const isStudentTenant = user && (user.role === 'student' && (user.tenant || user.college_id || !isMainPlatform));
    isPaid = !!isStudentTenant;
    
    if (!isPaid && userId) {
       const { hasFullAccess } = await import('../utils/paymentStatus');
       isPaid = await hasFullAccess(userId);
    }
  } catch (authErr) {
    console.error("Error checking payment status:", authErr);
  }

  try {
    // First, check if team has an assigned project
    try {
  const assignmentRes = await fetch(`${API_BASE_URL}/api/bootcamp/roles/teams/${teamId}`);
  if (assignmentRes.ok) {
  const assignmentData = await assignmentRes.json();
  if (assignmentData.ok && assignmentData.team && assignmentData.team.project_id) {
  // Team has assigned project - load tasks from project
  const projectRes = await fetch(`${API_BASE_URL}/api/bootcamp/roles/projects/${assignmentData.team.project_id}`);
  if (projectRes.ok) {
  const projectData = await projectRes.json();
  if (projectData.ok && projectData.project && projectData.project.tasks) {
  // Convert project tasks to expected format
  allTasks = projectData.project.tasks.map((task, taskIndex) => ({
  id: `task-${taskIndex}`,
  title: task.title || task.task_title || `Task ${taskIndex + 1}`,
  topic: task.title || task.task_title || task.topic || `Task ${taskIndex + 1}`,
  description: task.description || task.task_description ||"",
  subtopics: task.subtasks || task.subtopics || [],
  useCase: task.use_case || task.useCase ||"",
  futureUseCase: task.future_use_case || task.futureUseCase ||"",
  expectedOutput: task.expected_output || task.expectedOutput ||"",
  externalTools: [],
  projectName: projectData.project.title || projectData.project.project_name ||"Assigned Project",
  projectIndex: 0,
  phaseNo: task.phase_no || taskIndex + 1
  }));
  }
  }
  }
  }
  } catch (assignmentErr) {
  console.log('No assigned project found, falling back to JSON files:', assignmentErr.message);
  }
  
  // Fallback: Load from JSON files (for teams without assigned projects)
  if (allTasks.length === 0) {
    let userTeamData;
    try {
      const userTeamRes = await fetch(`${API_BASE_URL}/api/user-teams/user-team/${userId}`);
      if (!userTeamRes.ok) {
        if (userTeamRes.status === 404) {
          console.log(`ℹ️ User team not found for ${userId} (404). This is normal if user hasn't been assigned to a team yet.`);
          return [];
        }
        console.error(`❌ Failed to load user team data: ${userTeamRes.status} ${userTeamRes.statusText}`);
        return [];
      }
      userTeamData = await userTeamRes.json();
      
      if (!userTeamData.success || !userTeamData.data) {
        console.log(`ℹ️ User team data not found for ${userId}. This is normal if user hasn't been assigned to a team yet.`);
        return [];
      }
    } catch (teamErr) {
      if (teamErr.message.includes('404') || teamErr.message.includes('not found') || teamErr.message.includes('Failed to load')) {
        console.log(`ℹ️ User ${userId} is not assigned to a team yet. This is expected for new users.`);
        return [];
      }
      console.error('❌ Error loading user team:', teamErr);
      return [];
    }
    
    const teamData = userTeamData.data;
    const roleToFileName = (role) => {
      const roleMappings = {
        'DevOps Engineer': 'Senior DevOps team.json',
        'Junior DevOps Engineer': 'Junior DevOps Engineer team.json',
        'Senior DevOps': 'Senior DevOps team.json',
        'Senior DevOps - SRE Engineer': 'Senior DevOps - SRE Engineer team.json',
        'Cloud Engineer': 'Cloud Engineer team.json',
        'Cloud Solutions Architect': 'Cloud Solutions Architect team.json',
        'Cloud-Native Architect': 'Cloud-Native Architect team.json'
      };
      if (roleMappings[role]) return roleMappings[role];
      return role.replace(/\//g, '-').replace(/\s+/g, ' ').trim() + ' team.json';
    };
    
    const fileName = roleToFileName(teamData.role);
    const res = await fetch(`/teamroles/${fileName}`);
    if (!res.ok) {
      console.warn(`⚠️ No JSON file found for role "${teamData.role}". Team needs a project assigned by admin.`);
      return [];
    }
    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse team tasks JSON:', parseError);
      throw new Error('Invalid team tasks data format');
    }
    let projectData = null;
    if (Array.isArray(data) && data.length > 0) projectData = data[0];
    else if (data.project && data.tasks) projectData = data;
    else {
      console.warn('⚠️ No project found in team tasks data. Team needs a project assigned by admin.');
      return [];
    }
    if (!projectData || !projectData.tasks || !Array.isArray(projectData.tasks)) {
      console.warn('⚠️ Invalid project structure - no tasks found. Team needs a project assigned by admin.');
      return [];
    }
    allTasks = projectData.tasks.map((task, taskIndex) => ({
      id: `task-${taskIndex}`,
      title: task.title,
      topic: task.title.split(' - ')[1] || task.title,
      description: task.description,
      subtopics: task.subtasks || [],
      useCase: task.use_case ||"",
      futureUseCase: task.future_use_case ||"",
      expectedOutput: task.expected_output ||"",
      externalTools: [],
      projectName: projectData.project,
      projectIndex: 0,
      phaseNo: task.phase_no
    }));
  }
} catch (err) {
  console.error("Error loading tasks:", err);
}

   // If not paid, don't allow access (no free trial for bootcamp)
   if (!isPaid) {
      return [];
   }
  
  return allTasks;
}

async function explainTopicAPI({ userId, roleName, experienceLevel, task, language = 'en' }) {
 const url = `${API_BASE_URL}/api/bootcamp/roles/explain-topic`;
 
 // Add timeout to prevent hanging requests
 const controller = new AbortController();
 const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
 
 try {
 const res = await fetch(url, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify({ userId, roleName, experienceLevel, task, language, taskDescription: task?.description || "", taskSubtopics: task?.subtopics || [], taskUseCase: task?.useCase || "", taskFutureUseCase: task?.futureUseCase || "", taskExpectedOutput: task?.expectedOutput || "" }),
 signal: controller.signal,
 });
 
 clearTimeout(timeoutId);

 // Handle 504 Gateway Timeout specifically
 if (res.status === 504) {
 throw new Error("The explanation is taking longer than expected. This might be due to high server load. Please try again in a moment.");
 }

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) {
 // Provide more helpful error messages
 if (res.status === 504) {
 throw new Error("The explanation request timed out. Please try again with a simpler topic or try again later.");
 }
 throw new Error(data.error || `HTTP ${res.status}`);
 }
 return data;
 } catch (err) {
 clearTimeout(timeoutId);
 // Handle abort (timeout) errors
 if (err.name === 'AbortError') {
 throw new Error("Request timed out. The explanation is taking too long. Please try again.");
 }
 // Re-throw other errors
 throw err;
 }
}

async function generateVideoAPI({ userId, task, courseType, experienceLevel, language }) {
 const url = `${API_BASE_URL}/api/video/generate`;
 
 try {
 const res = await fetch(url, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify({ userId, task, courseType, experienceLevel, language, duration: 75 })
 });

 // Check if response has JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 console.error('Non-JSON response:', text.substring(0, 500));
 throw new Error(`Video generation service unavailable. Please try again later.`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) {
 throw new Error(data.error || data.message || `HTTP ${res.status}`);
 }
 return data;
 } catch (error) {
 if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
 throw new Error('Video generation service is temporarily unavailable. Please try again in a few minutes.');
 }
 throw error;
 }
}

async function visualizeOutputAPI({ userId, bunchName, experienceLevel, task, hint }) {
 const url = `${API_BASE_URL}/api/bootcamp/roles/visualize`;
 
 // Add timeout to prevent hanging requests
 const controller = new AbortController();
 const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
 
 try {
 const res = await fetch(url, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify({
 userId,
 roleName: bunchName,
 experienceLevel,
 task,
 hint: hint ||"",
 taskDescription: task?.description || "",
 taskSubtopics: task?.subtopics || [],
 taskUseCase: task?.useCase || "",
 }),
 signal: controller.signal,
 });

 clearTimeout(timeoutId);

 // Handle 504 Gateway Timeout specifically
 if (res.status === 504) {
 throw new Error("The visualization is taking longer than expected. This might be due to high server load. Please try again in a moment.");
 }

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) {
 // Provide more helpful error messages
 if (res.status === 504) {
 throw new Error("The visualization request timed out. Please try again with a simpler request or try again later.");
 }
 throw new Error(data.error || `HTTP ${res.status}`);
 }
 return data;
 } catch (err) {
 clearTimeout(timeoutId);
 // Handle abort (timeout) errors
 if (err.name === 'AbortError') {
 throw new Error("Request timed out. The visualization is taking too long. Please try again.");
 }
 // Re-throw other errors
 throw err;
 }
}

async function reviewTaskWithAI({ userId, bunchName, experienceLevel, task, docFile, imgFile, comments, githubUrl, submissionType }) {
 const url = `${API}/api/user-ai-review`;
 try {
 const fd = new FormData();
 fd.append("userId", userId);
 fd.append("bunchName", bunchName);
 fd.append("experienceLevel", experienceLevel);
 fd.append("taskId", task.id);
 fd.append("taskTitle", task.title);
 fd.append("expectedOutput", task.expectedOutput ||"");
 fd.append("useCase", task.useCase ||"");
 fd.append("futureUseCase", task.futureUseCase ||"");
 fd.append("subtopics", JSON.stringify(task.subtopics || []));
 fd.append("submissionType", submissionType ||"files");
 fd.append("githubUrl", githubUrl ||"");
 fd.append("comments", comments ||"");
 if (docFile) fd.append("docFile", docFile);
 if (imgFile) fd.append("imgFile", imgFile);

 const sessionToken = localStorage.getItem('session_token');
 const headers = {};
 if (sessionToken) {
   headers['Authorization'] = `Bearer ${sessionToken}`;
 }

 const res = await fetch(url, { method:"POST", body: fd, headers });
 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data;
 } catch (error) {
 console.error("TeamRoles reviewTaskWithAI error:", error);
 throw new Error(
 error?.message
 ? `Submission failed: ${error.message}`
 :"Submission failed. Ensure your work is aligned with the task and try again."
 );
 }
}

async function loadTeamChatMessages(teamId, userId) {
 const res = await fetch(`${API}/api/teams/${teamId}/chat/messages`, {
 headers: { 'user-id': userId }
 });
 const data = await res.json();
 if (!res.ok || !data.success) throw new Error('Failed to load chat messages');
 return data.messages;
}

async function sendTeamChatMessage(teamId, userId, message, messageType = 'text', fileUrl = null, fileName = null, replyToId = null) {
 const res = await fetch(`${API}/api/teams/${teamId}/chat/messages`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json'
 },
 body: JSON.stringify({ 
 message, 
 message_type: messageType, 
 file_url: fileUrl, 
 file_name: fileName, 
 reply_to_id: replyToId,
 user_id: userId // ✅ Send user_id in body for authentication
 })
 });
 const data = await res.json();
 if (!res.ok || !data.success) throw new Error(data.error || 'Failed to send message');
 return data.message;
}

export default function TeamRoles() {
  const { isMainPlatform } = useTenant();
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id') || (() => {
 try {
 const u = JSON.parse(localStorage.getItem("user") ||"{}");
 return u.userId || u.user_id ||"";
 } catch {
 return"";
 }
 })();
 
 const [loading, setLoading] = useState(true);
 const [notAllowed, setNotAllowed] = useState("");

 // Check payment status before loading dashboard
 useEffect(() => {
 let alive = true;
 (async () => {
 if (!userId) return;
 
 try {
   // ✅ NEW: Student Tenant Access check first
   const user = JSON.parse(localStorage.getItem("user") || '{}');
   const isMainPlatform = window.location.hostname === 'theskillgenie.com' || 
                          window.location.hostname === 'www.theskillgenie.com' || 
                          window.location.hostname === 'localhost';

   if (user && (user.role === 'student' && (user.tenant || !isMainPlatform))) {
     console.log(`[TeamRoles] Full access granted to Student Tenant: ${userId}`);
     setLoading(false);
     setNotAllowed("");
     return;
   }

 const hasAccess = await hasFullAccess(userId);
 if (!hasAccess && alive) {
 const proceed = await redirectToPaymentIfNeeded(userId, navigate);
 if (!proceed && alive) {
 setLoading(false);
 setNotAllowed("Premium subscription required to access this dashboard.");
 return;
 }
 }
 } catch (err) {
 console.error("Payment check failed:", err);
 // Continue loading - backend will also check
 }
 })();
 return () => { alive = false; };
 }, [userId, navigate]);
  const [tasks, setTasks] = useState([]);
  const [completedTaskIds, setCompletedTaskIds] = useState([]);
  const [reviewStateById, setReviewStateById] = useState({});
  const [certificateEligible, setCertificateEligible] = useState(false);
  const [viewIndex, setViewIndex] = useState(0);

  // Derived state (Moved to top to prevent ReferenceError)
  const totalTasks = tasks.length;
  const doneSet = useMemo(() => new Set(completedTaskIds), [completedTaskIds, tasks]);
  const completedCount = useMemo(() => {
    let c = 0;
    for (let i = 0; i < totalTasks; i++) {
      const id = tasks[i]?.id;
      if (id && doneSet.has(id)) c++;
      else break;
    }
    return c;
  }, [doneSet, tasks, totalTasks]);
  const completedTasks = completedCount;
  const unlockedIndex = clamp(completedCount, 0, Math.max(0, totalTasks - 1));
  const percent = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const progressPercentage = percent;
  const viewingTask = viewIndex != null ? tasks[viewIndex] : null;

  const [userName, setUserName] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("");
  const [bunchName, setBunchName] = useState("");
  const technology = bunchName || "Technology";
  const [githubRepoUrl, setGithubRepoUrl] = useState("");
  const [invitationStatus, setInvitationStatus] = useState("");

  const syncInvitationStatus = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/bootcamp/sync-invitation/${userId}`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setInvitationStatus(data.invitationStatus || data.invitation_status || "");
        setGithubRepoUrl(data.githubRepoUrl || data.github_repo_url || "");
      }
    } catch (err) {
      console.error("Failed to sync invitation status", err);
    }
  };

const [showModal, setShowModal] = useState(false);
 const [modalTab, setModalTab] = useState("overview");
 const [showMentor, setShowMentor] = useState(false);
 const [mentorMessages, setMentorMessages] = useState([]);
 const [topicExplanation, setTopicExplanation] = useState(null);
 
 // TTS (Text-to-Speech) state
 const [ttsLanguage, setTtsLanguage] = useState('en');
 const [isSpeaking, setIsSpeaking] = useState(false);
 const [speechSynthesis, setSpeechSynthesis] = useState(null);
 const speechUtteranceRef = useRef(null);

 // Initialize speech synthesis
 useEffect(() => {
 if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
 setSpeechSynthesis(window.speechSynthesis);
 }
 }, []);

 // Language options for TTS
 const ttsLanguages = [
 { code: 'en', name: 'English', voice: 'en-US' },
 // Indian Languages
 { code: 'hi', name: 'Hindi (हिंदी)', voice: 'hi-IN' },
 { code: 'ta', name: 'Tamil (தமிழ்)', voice: 'ta-IN' },
 { code: 'te', name: 'Telugu (తెలుగు)', voice: 'te-IN' },
 { code: 'kn', name: 'Kannada (ಕನ್ನಡ)', voice: 'kn-IN' },
 { code: 'ml', name: 'Malayalam (മലയാളം)', voice: 'ml-IN' },
 { code: 'bn', name: 'Bengali (বাংলা)', voice: 'bn-IN' },
 { code: 'gu', name: 'Gujarati (ગુજરાતી)', voice: 'gu-IN' },
 { code: 'mr', name: 'Marathi (मराठी)', voice: 'mr-IN' },
 { code: 'pa', name: 'Punjabi (ਪੰਜਾਬੀ)', voice: 'pa-IN' },
 { code: 'ur', name: 'Urdu (اردو)', voice: 'ur-IN' },
 { code: 'or', name: 'Odia (ଓଡ଼ିଆ)', voice: 'or-IN' },
 { code: 'as', name: 'Assamese (অসমীয়া)', voice: 'as-IN' },
 // Other Languages
 { code: 'es', name: 'Spanish', voice: 'es-ES' },
 { code: 'fr', name: 'French', voice: 'fr-FR' },
 { code: 'de', name: 'German', voice: 'de-DE' },
 { code: 'it', name: 'Italian', voice: 'it-IT' },
 { code: 'pt', name: 'Portuguese', voice: 'pt-BR' },
 { code: 'ja', name: 'Japanese', voice: 'ja-JP' },
 { code: 'ko', name: 'Korean', voice: 'ko-KR' },
 { code: 'zh', name: 'Chinese', voice: 'zh-CN' },
 { code: 'ar', name: 'Arabic', voice: 'ar-SA' },
 { code: 'ru', name: 'Russian', voice: 'ru-RU' },
 ];

 // Stop speaking
 const stopSpeaking = () => {
 if (speechSynthesis && speechSynthesis.speaking) {
 speechSynthesis.cancel();
 setIsSpeaking(false);
 }
 };

 // Speak text
 const speakText = (text, lang = ttsLanguage) => {
 if (!speechSynthesis || !text) return;
 stopSpeaking();
 const cleanText = text.replace(/```[\s\S]*?```/g, '').replace(/`[^`]+`/g, '').replace(/#{1,6}\s+/g, '').replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').trim();
 if (!cleanText) return;
 const utterance = new SpeechSynthesisUtterance(cleanText);
 const selectedLang = ttsLanguages.find(l => l.code === lang) || ttsLanguages[0];
 utterance.lang = selectedLang.voice;
 utterance.rate = 0.9;
 utterance.pitch = 1;
 utterance.volume = 1;
 const voices = speechSynthesis.getVoices();
 const voice = voices.find(v => v.lang.startsWith(selectedLang.code)) || voices.find(v => v.lang.includes(selectedLang.code)) || voices[0];
 if (voice) utterance.voice = voice;
 utterance.onend = () => setIsSpeaking(false);
 utterance.onerror = () => setIsSpeaking(false);
 speechUtteranceRef.current = utterance;
 speechSynthesis.speak(utterance);
 setIsSpeaking(true);
 };

 // Cleanup on unmount
 useEffect(() => {
 return () => {
 if (speechSynthesis && speechSynthesis.speaking) {
 speechSynthesis.cancel();
 }
 };
 }, [speechSynthesis]);
 
 // File upload states
 const [docFile, setDocFile] = useState(null);
 const [imgFile, setImgFile] = useState(null);
 const [comments, setComments] = useState("");
 const [submittingReview, setSubmittingReview] = useState(false);
 const [reviewError, setReviewError] = useState("");
 const [githubUrl, setGithubUrl] = useState("");
 const [submissionType, setSubmissionType] = useState("github");
 
 // Task completion tracking
 const { markTaskCompleted } = useTaskCompletion(userId, 'team');
 
 // 🎯 LLM Trial state
 const [llmTrialStatus, setLlmTrialStatus] = useState(null);
 const [showTrialModal, setShowTrialModal] = useState(false);
 const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

 // Voice Assistant
 const [vaInitialMessage, setVaInitialMessage] = useState("");
 const {
   isVoiceAssistantOpen,
   openVoiceAssistant,
   closeVoiceAssistant,
   handleVoiceCommand,
   handleVoiceNavigation
 } = useVoiceAssistant();

 // Helper to open Voice Assistant with subtopic explanation
 const explainWithAI = async (subtopic) => {
   try {
     setVaInitialMessage(`Generating explanation for: ${subtopic}...`);
     openVoiceAssistant();
     
     // Fetch detailed explanation for the subtopic
     const res = await fetch(`${API_BASE_URL}/chat`, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'user-id': userId
       },
       body: JSON.stringify({
         message: `Explain the subtopic "${subtopic}" for the topic "${tasks[viewIndex]?.title || tasks[viewIndex]?.topic}" in ${bunchName}. Provide a detailed explanation that I can read.`,
         langMode: "fixed",
         langCode: "en-US",
         userId: userId
       })
     });
     
     if (res.ok) {
       const data = await res.json();
       setVaInitialMessage(data.reply);
     } else {
       setVaInitialMessage(`Sorry, I couldn't generate an explanation for "${subtopic}" right now. Please try again.`);
     }
   } catch (err) {
     console.error("Failed to explain subtopic:", err);
     setVaInitialMessage("Error connecting to AI Assistant.");
   }
 };
 const videoStatusRef = useRef("");
 const [explanationLoading, setExplanationLoading] = useState(false);
 const [explanationLanguage] = useState('en'); 
 const [explanationUsage, setExplanationUsage] = useState(null);
 const [videoUsage, setVideoUsage] = useState(null);
 const [videoGenerating, setVideoGenerating] = useState(false);
 const [videoUrl, setVideoUrl] = useState(null);
 const [videoStatus, setVideoStatus] = useState(""); //"generating","queued","ready","error"
 const [videoMessage, setVideoMessage] = useState("");
 const [videoError, setVideoError] = useState("");
 const videoPollRef = useRef(null);
 const videoBaseUrlRef = useRef(null);
 const videoPrimaryUrlRef = useRef(null);
 const videoOriginUrlRef = useRef(null);
 const videoPollInFlightRef = useRef(false);
 const videoPollStartedAtRef = useRef(null);
 const videoErrorRetriesRef = useRef(0);
 const [visualizing, setVisualizing] = useState(false);
 const [visualUrl, setVisualUrl] = useState("");
 const [visualGeneratedAt, setVisualGeneratedAt] = useState(null);
 const [visualHint, setVisualHint] = useState("");
 
  // Team-specific state
 const [teamId, setTeamId] = useState(null);
 const [teamMembers, setTeamMembers] = useState([]);
 const [showTeamChat, setShowTeamChat] = useState(false);
 const [teamChatMessages, setTeamChatMessages] = useState([]);
 const [teamChatText, setTeamChatText] = useState("");
 const [teamChatFile, setTeamChatFile] = useState(null);
 const [sendingTeamMessage, setSendingTeamMessage] = useState(false);
 const [mobileOpen, setMobileOpen] = useState(false);

 useEffect(() => {
 document.body.style.overflow = mobileOpen ?"hidden" :"";
 return () => (document.body.style.overflow ="");
 }, [mobileOpen]);

 // Map team members to Person 1-4 for subtasks
 const getPersonAssignment = (personStr) => {
 if (!teamMembers.length) return personStr;
 
 const personMap = {
 'Person 1': teamMembers[0]?.name || 'Person 1',
 'Person 2': teamMembers[1]?.name || 'Person 2', 
 'Person 3': teamMembers[2]?.name || 'Person 3',
 'Person 4': teamMembers[3]?.name || 'Person 4'
 };
 
 return personMap[personStr] || personStr;
 };

 useEffect(() => {
 if (!userId) {
 setNotAllowed("Please log in to access team workspace");
 setLoading(false);
 return;
 }

 let alive = true;
 const loadData = async () => {
 try {
 // Get user's team ID
 const teamsRes = await fetch(`${API}/api/user-teams/user-team/${userId}`);
 const teamsData = await teamsRes.json();
 
 if (teamsData.success && teamsData.data) {
 const userTeam = teamsData.data;
 console.log('👥 User team data:', userTeam);
 
 if (userTeam) {
 setTeamId(userTeam.team_id);
 console.log('👥 Set teamId to:', userTeam.team_id);
 
 // Load team tasks
 const teamTasks = await loadTeamTasks(userTeam.team_id, userId);
 if (alive) setTasks(teamTasks);
 
 // Load user context
 const ctx = await fetchUserContext(userId, userTeam.team_id);
 if (alive) {
 setUserName(ctx.user.name || ctx.user.full_name || 'Unknown User');
 setExperienceLevel(ctx.enrollment.experienceLevel || 'Intermediate');
 setBunchName(ctx.enrollment.bunchName || 'Software Engineer');
 setGithubRepoUrl(ctx.enrollment.githubRepoUrl || "");
 setInvitationStatus(ctx.enrollment.invitationStatus || "");
 if (ctx.enrollment.githubRepoUrl) {
   setGithubUrl(ctx.enrollment.githubRepoUrl);
   setSubmissionType("github");
 }
 }
 
 // Load progress
 const progress = readProgress(userId, `team-${userTeam.team_id}`);
 if (alive) {
 setCompletedTaskIds(progress.completedTaskIds);
 setReviewStateById(progress.reviewStateById);
 
 // Sync existing progress to backend for team leaderboard
 if (progress.completedTaskIds.length > 0) {
 syncProgressToBackend(userId, `team-${userTeam.team_id}`, progress.completedTaskIds, tasks.length);
 }
 }

 // Load mentor chat (using teamId instead of role)
 const chat = readChat(userId, `team-${userTeam.team_id}`);
 if (alive) setMentorMessages(chat);

 // Load team members
 const teamRes = await fetch(`${API}/api/teams/${userTeam.team_id}/members`);
 const teamData = await teamRes.json();
 if (teamData.success && teamData.members) {
 if (alive) setTeamMembers(teamData.members);
 }

 // Load team chat messages
 const messages = await loadTeamChatMessages(userTeam.team_id, userId);
 if (alive) setTeamChatMessages(messages);
 
 // Set certificate eligibility
 if (alive) {
 setCertificateEligible(teamTasks.length > 0 && (progress.completedTaskIds?.length || 0) >= teamTasks.length);
 }
 } else {
 if (alive) setNotAllowed("You are not part of this team. Please contact your administrator to join a team.");
 }
 }

 if (alive) setLoading(false);
 } catch (err) {
 if (alive) {
 setNotAllowed(String(err?.message ||"Access restricted")); 
 setLoading(false);
 }
 }
 };
 
 loadData();
 return () => { alive = false; };
 }, [userId]);

 // Auto-refresh team chat messages
 useEffect(() => {
 if (!teamId || !showTeamChat) return;
 
 const interval = setInterval(async () => {
 try {
 const messages = await loadTeamChatMessages(teamId, userId);
 setTeamChatMessages(messages);
 } catch (err) {
 console.error('Failed to refresh team chat:', err);
 }
 }, 2000);

 return () => clearInterval(interval);
 }, [teamId, showTeamChat, userId]);

 // ✅ Check LLM Trial Status on component mount and auto-show welcome modal
 useEffect(() => {
 if (userId) {
 (async () => {
 const status = await checkLLMTrialStatusUtil(userId);
 setLlmTrialStatus(status);
 console.log('🎯 LLM Trial Status:', status);
 
 // Auto-show welcome modal for new trial users (first time only)
 if (status?.hasActiveTrial && status?.source === 'trial') {
 const welcomeShownKey = `llm_trial_welcome_shown_${userId}`;
 const welcomeShown = localStorage.getItem(welcomeShownKey);
 if (!welcomeShown) {
 setShowTrialModal(true);
 localStorage.setItem(welcomeShownKey, 'true');
 }
 }
 })();
 }
 }, [userId]);

 const cacheBustUrl = (baseUrl) => `${baseUrl}${baseUrl.includes("?") ?"&" :"?"}v=${Date.now()}`;
 const toPreferredPlaybackUrl = (url) => {
 if (!url) return url;
 try {
 const parsed = new URL(url);
 if (parsed.hostname?.includes("amazonaws.com") && typeof window !=="undefined") {
 return `${window.location.origin}${parsed.pathname}${parsed.search ||""}`;
 }
 return url;
 } catch (err) {
 if (url.startsWith("/") && typeof window !=="undefined") {
 return `${window.location.origin}${url}`;
 }
 return url;
 }
 };
 const stopVideoPolling = () => {
 if (videoPollRef.current) {
 clearInterval(videoPollRef.current);
 videoPollRef.current = null;
 }
 videoPollInFlightRef.current = false;
 videoPollStartedAtRef.current = null;
 videoPrimaryUrlRef.current = null;
 videoOriginUrlRef.current = null;
 };
 const startVideoPolling = (primaryUrl, originProbeUrl, playbackBaseUrl) => {
 stopVideoPolling();
 const preferredPrimary = playbackBaseUrl || primaryUrl || null;
 videoPrimaryUrlRef.current = preferredPrimary;
 videoOriginUrlRef.current = originProbeUrl || null;
 videoBaseUrlRef.current = playbackBaseUrl || originProbeUrl || primaryUrl || null;
 videoPollStartedAtRef.current = Date.now();

 const checkAvailability = async () => {
 // Use ref to track status to avoid closure issues
 const currentStatus = videoStatusRef.current;
 if (!videoBaseUrlRef.current || currentStatus ==="ready" || currentStatus ==="error") return;
 if (videoPollInFlightRef.current) return;
 videoPollInFlightRef.current = true;
 
 // Use backend endpoint to check if video exists (no CORS issues)
 const baseUrl = videoBaseUrlRef.current;
 if (!baseUrl) {
 videoPollInFlightRef.current = false;
 return;
 }
 
 try {
 // Check via backend endpoint - this avoids CORS issues
 const checkUrl = `${API_BASE_URL}/api/video/check-exists?url=${encodeURIComponent(baseUrl)}`;
 const res = await fetch(checkUrl, {
 method:"GET",
 cache:"no-store",
 });
 
 if (!res.ok) {
 videoPollInFlightRef.current = false;
 return;
 }
 
 const data = await res.json();
 
 if (data.ok && data.exists && data.status === 'ready') {
 // Video is ready!
 stopVideoPolling();
 const proxiedBaseUrl = getProxiedVideoUrl(baseUrl);
 const finalUrl = cacheBustUrl(proxiedBaseUrl);
 videoStatusRef.current ="ready";
 videoBaseUrlRef.current = baseUrl;
 setVideoUrl(finalUrl);
 setVideoStatus("ready");
 if (data.usage) {
   setVideoUsage(data.usage);
 }
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 videoErrorRetriesRef.current = 0;
 console.log("[Video Polling] Video confirmed ready via backend:", finalUrl);
 return;
 } else if (data.ok === false || data.error || data.status === 'failed') {
 // Video generation failed or error occurred
 stopVideoPolling();
 videoStatusRef.current ="error";
 setVideoStatus("error");
 const errorMessage = data.error || data.message ||"Video generation failed. Please try regenerating.";
 setVideoError(errorMessage);
 setVideoMessage("");
 console.error("[Video Polling] Video generation failed:", {
 error: data.error,
 message: data.message,
 status: data.status
 });
 return;
 } else {
 // Video not ready yet, continue polling
 console.debug("[Video Polling] Video not ready yet, status:", data.status,"exists:", data.exists);
 }
 } catch (err) {
 // Log errors but don't stop polling immediately - video might still be processing
 console.warn("[Video Polling] Check failed:", err?.message || err);
 // If we've been polling for more than 3 minutes and getting errors, it might be a real issue
 const startedAt = videoPollStartedAtRef.current;
 if (startedAt && Date.now() - startedAt > 3 * 60 * 1000) {
 console.error("[Video Polling] Persistent errors after 3 minutes - video generation may have failed");
 }
 } finally {
 videoPollInFlightRef.current = false;
 }

 const startedAt = videoPollStartedAtRef.current;
 // Reduce timeout to 5 minutes since video generation should complete faster
 if (startedAt && Date.now() - startedAt > 5 * 60 * 1000) {
 stopVideoPolling();
 videoStatusRef.current ="error";
 setVideoStatus("error");
 setVideoError("Video generation timed out after 5 minutes. The video may have failed to generate. Please try regenerating.");
 setVideoMessage("");
 console.warn("[Video Polling] Timeout after 5 minutes");
 }
 };

 checkAvailability();
 videoPollRef.current = setInterval(checkAvailability, 12000);
 };
 useEffect(() => {
 return () => {
 stopVideoPolling();
 };
 }, []);

 const handleVideoLoaded = () => {
 console.log("[Video Player] Video loaded successfully");
 stopVideoPolling();
 videoStatusRef.current ="ready";
 setVideoStatus("ready");
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 videoErrorRetriesRef.current = 0;
 };
 const handleVideoLoadError = (e) => {
 const currentStatus = videoStatusRef.current || videoStatus;
 const currentUrl = videoUrl;
 const base = videoBaseUrlRef.current;
 
 // Don't fallback to direct CloudFront URL as it will cause CORS errors
 // The proxy URL should work - if it doesn't, the video might still be processing
 // Continue with normal error handling
 
 if (currentStatus ==="queued" || currentStatus ==="generating") {
 // Video is still rendering, this is expected - don't show error yet
 // Silently ignore errors during queued/generating status
 return;
 }
 
 // Only log errors if video is not in queued/generating status
 console.warn("[Video Player] Video load error:", {
 status: currentStatus,
 url: currentUrl,
 baseUrl: base,
 error: e?.target?.error,
 networkState: e?.target?.networkState,
 readyState: e?.target?.readyState,
 retries: videoErrorRetriesRef.current
 });
 
 // If status is"ready" but video fails, it might be a false positive from polling
 // Reset to"queued" and let polling continue
 if (currentStatus ==="ready" && videoErrorRetriesRef.current < 3) {
 console.log("[Video Player] Video marked ready but failed to load, resetting to queued");
 videoStatusRef.current ="queued";
 setVideoStatus("queued");
 setVideoMessage("Video is still processing, please wait...");
 // Don't increment retry count for this case
 return;
 }
 
 if (base && videoErrorRetriesRef.current < 5) {
 videoErrorRetriesRef.current += 1;
 console.log(`[Video Player] Retrying video load (attempt ${videoErrorRetriesRef.current}/5)`);
 setTimeout(() => {
 // Always use proxy URL to avoid CORS errors
 const retryUrl = cacheBustUrl(getProxiedVideoUrl(base));
 console.log(`[Video Player] Retrying with proxy URL: ${retryUrl}`);
 setVideoUrl(retryUrl);
 }, 3000 * videoErrorRetriesRef.current);
 } else {
 console.error("[Video Player] Video failed to load after retries", {
 finalUrl: currentUrl,
 baseUrl: base,
 status: currentStatus
 });
 setVideoStatus("error");
 setVideoError(`Video failed to load. The video may not be available yet or the URL may be incorrect. Please try regenerating the video.`);
 // Reset retry counter for next attempt
 videoErrorRetriesRef.current = 0;
 }
 };

 const handleTaskComplete = (index) => {
 const newCompleted = [...completedTaskIds];
 const taskId = `${index}`;
 
 if (newCompleted.includes(taskId)) {
 newCompleted.splice(newCompleted.indexOf(taskId), 1);
 } else {
 newCompleted.push(taskId);
 }
 
 setCompletedTaskIds(newCompleted);
 writeProgress(userId, `team-${teamId}`, { completedTaskIds: newCompleted, reviewStateById });
 };

 const openCurrentTask = (tab ="overview") => {
 const currentIndex = Math.min(completedTasks, totalTasks - 1);
 setViewIndex(currentIndex);
 setModalTab(tab);
 setShowModal(true);
 };

 const handleTaskSubmission = async () => {
 const currentTask = tasks[viewIndex];
 if (!currentTask) return;

 setReviewError("");
 setSubmittingReview(true);

 const pendingReview = { ...reviewStateById, [currentTask.id]: { status:"pending" } };
 setReviewStateById(pendingReview);
 writeProgress(userId, `team-${teamId}`, { completedTaskIds, reviewStateById: pendingReview });

 try {
 // Use provisioned GitHub URL if available
    const finalGithubUrl = githubRepoUrl || githubUrl;
    const finalSubmissionType = githubRepoUrl ? "github" : submissionType;

    const result = await reviewTaskWithAI({
      userId,
      bunchName,
      experienceLevel,
      task: currentTask,
      docFile,
      imgFile,
      comments,
      githubUrl: finalGithubUrl,
      submissionType: finalSubmissionType,
    });

 const nextReview = {
 status: result.passed ?"passed" :"failed",
 feedback: result.feedback ||"",
 score: typeof result.score ==="number" ? result.score : null,
 breakdown: result.breakdown || null,
 };

 let nextCompletedIds = completedTaskIds;
 if (result.passed && !completedTaskIds.includes(currentTask.id)) {
 nextCompletedIds = Array.from(new Set([...completedTaskIds, currentTask.id]));
 setCompletedTaskIds(nextCompletedIds);
 markTaskCompleted();
 syncProgressToBackend(userId, `team-${teamId}`, nextCompletedIds, tasks.length);
 }

 const updatedReviewState = { ...pendingReview, [currentTask.id]: nextReview };
 setReviewStateById(updatedReviewState);
 writeProgress(userId, `team-${teamId}`, { completedTaskIds: nextCompletedIds, reviewStateById: updatedReviewState });

 setModalTab("feedback");
 } catch (error) {
 console.error("Error submitting task:", error);
 setReviewError(error.message ||"Submission failed. Please try again.");
 const { [currentTask.id]: _removed, ...rest } = pendingReview;
 setReviewStateById(rest);
 writeProgress(userId, `team-${teamId}`, { completedTaskIds, reviewStateById: rest });
 } finally {
 setSubmittingReview(false);
 }
 };

 // Voice command handlers
 const onVoiceCommand = (command) => {
 const result = handleVoiceCommand(command);
 
 switch (result.type) {
 case 'open-mentor':
 setShowMentor(true);
 break;
 case 'open-video':
 openCurrentTask('video');
 break;
 case 'open-resources':
 openCurrentTask('resources');
 break;
 case 'explain-current':
 // Generate explanation for current task using selected language
 if (tasks[viewIndex]) {
 explainTopic();
 }
 break;
 case 'open-submit':
 openCurrentTask('submit');
 break;
 case 'open-feedback':
 openCurrentTask('feedback');
 break;
 case 'open-visualize':
 openCurrentTask('visualize');
 break;
 case 'start-task':
 if (tasks.length > 0) {
 openCurrentTask('overview');
 }
 break;
 case 'next-task':
 if (viewIndex !== null && viewIndex < tasks.length - 1) {
 setViewIndex(viewIndex + 1);
 setShowModal(true);
 }
 break;
 case 'previous-task':
 if (viewIndex !== null && viewIndex > 0) {
 setViewIndex(viewIndex - 1);
 setShowModal(true);
 }
 break;
 case 'get-help':
 setShowMentor(true);
 break;
 default:
 // Handle unknown commands
 break;
 }
 };

 const onVoiceNavigate = (path) => {
 const result = handleVoiceNavigation(path);
 if (result.type === 'navigate') {
 navigate(result.path);
 }
 };

 const handleTaskClick = (index) => {
 setViewIndex(index);
 setModalTab("overview");
 setShowModal(true);
 // Reset file upload states when opening a new task
 setDocFile(null);
 setImgFile(null);
 setComments("");
 };

 const handleSendTeamMessage = async () => {
 if (!teamChatText.trim() && !teamChatFile) return;
 
 setSendingTeamMessage(true);
 try {
 let fileUrl = null;
 let fileName = null;
 
 if (teamChatFile) {
 fileName = teamChatFile.name;
 fileUrl = URL.createObjectURL(teamChatFile);
 }

 const newMessage = await sendTeamChatMessage(
 teamId, 
 userId,
 teamChatText, 
 teamChatFile ? 'file' : 'text',
 fileUrl,
 fileName
 );
 
 setTeamChatMessages(prev => [...prev, newMessage]);
 setTeamChatText("");
 setTeamChatFile(null);
 } catch (err) {
 console.error('Failed to send team message:', err);
 alert('Failed to send message: ' + err.message);
 } finally {
 setSendingTeamMessage(false);
 }
 };

 const sendMentorMessage = async ({ text, imgFile }) => {
  if (!text && !imgFile) return;
  const you = { id: `u-${Date.now()}`, role: "user", text, imagePreview: imgFile ? URL.createObjectURL(imgFile) : null, when: new Date().toISOString() };
  const thinking = { id: `t-${Date.now()}`, role: "assistant", text: "Thinking…", typing: true, when: new Date().toISOString() };
  const add = (arr) => { setMentorMessages(arr); writeChat(userId, `team-${teamId}`, arr); };

  const now = [...mentorMessages, you, thinking];
  add(now);
  const task = tasks[viewIndex];

  // Enhance text with technology context if it's a general question
  let enhancedText = text;
  if (text && text.length < 100 && !text.toLowerCase().includes(technology.toLowerCase())) {
    enhancedText = `Regarding ${technology}: ${text}`;
  }

  const res = await askMentorAPI({ userId, technology: bunchName, experienceLevel, task, text: enhancedText, imgFile, history: mentorMessages });
  const final = [...now.filter((m) => !m.typing), { id: `a-${Date.now()}`, role: "assistant", text: res.answer || "I'm here to help.", when: new Date().toISOString() }];
  add(final);
};


 const generateVideo = async () => {
 if (!tasks[viewIndex]) return;

 // Generate video directly without confirmation popup
 setVideoGenerating(true);
 videoStatusRef.current ="generating";
 setVideoStatus("generating");
 setVideoMessage("");
 setVideoError("");
 stopVideoPolling();
 setVideoUrl(null);
 
 try {
 // Robust userId resolution
 const storedUserRaw = localStorage.getItem('user');
 const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
 const uid = (
 userId ||
 storedUser?.userId ||
 storedUser?.user_id ||
 localStorage.getItem('userId') ||
 localStorage.getItem('user_id') ||
 'guest'
 );
 // Determine preferred narration language (fallback to browser)
 const pref = (localStorage.getItem('preferred_language') || navigator.language || 'en-US').toLowerCase();
 const langMap = {
 'te': 'te-IN', 'te-in': 'te-IN', 'telugu': 'te-IN',
 'ta': 'ta-IN', 'ta-in': 'ta-IN', 'tamil': 'ta-IN',
 'hi': 'hi-IN', 'hi-in': 'hi-IN', 'hindi': 'hi-IN',
 'mr': 'mr-IN', 'mr-in': 'mr-IN', 'marathi': 'mr-IN',
 'ml': 'ml-IN', 'ml-in': 'ml-IN', 'malayalam': 'ml-IN',
 'en': 'en-US', 'en-us': 'en-US', 'english': 'en-US'
 };
 const language = langMap[pref] || langMap[pref.split('-')[0]] || 'en-US';

 const response = await generateVideoAPI({
 userId: uid,
 task: tasks[viewIndex],
 courseType: bunchName,
 experienceLevel,
 language
 });
 
 const rawVideoPath = response.videoUrl || response.expectedUrl || response.playbackUrl;
 const videoPath = toPreferredPlaybackUrl(rawVideoPath);
 if (!videoPath) {
 throw new Error("Video generation service did not return a video URL.");
 }
 const absoluteUrl = videoPath.startsWith("http")
 ? videoPath
 : `${API_BASE_URL}${videoPath.startsWith("/") ? videoPath : `/${videoPath}`}`;
 const originProbeUrl = (response.originUrl && response.originUrl.startsWith("http"))
 ? response.originUrl
 : null;
 const rawPlaybackBase =
 (response.cdnUrl && response.cdnUrl.startsWith("http"))
 ? response.cdnUrl
 : rawVideoPath;
 const playbackBaseUrl = toPreferredPlaybackUrl(rawPlaybackBase) || absoluteUrl;

 // Use proxy URL for CloudFront videos to avoid CORS issues
 const proxiedPlaybackUrl = getProxiedVideoUrl(playbackBaseUrl);
 const proxiedOriginUrl = originProbeUrl ? getProxiedVideoUrl(originProbeUrl) : null;

 if (response.queued) {
 videoStatusRef.current ="queued";
 setVideoStatus("queued");
 setVideoMessage("Rendering video… This usually takes 1-3 minutes. We'll refresh automatically.");
 // Don't set video URL yet - wait until backend confirms it's ready
 // This prevents the video element from trying to load a non-existent video
 setVideoUrl(null);
 // Start polling backend endpoint to detect when video is ready (no CORS issues)
 startVideoPolling(playbackBaseUrl, originProbeUrl, playbackBaseUrl);
 setModalTab("video");
 console.log("[Video Generation] Video queued, polling backend to detect when ready:", playbackBaseUrl);
 alert(
 `🎬 Video rendering started for"${tasks[viewIndex].topic || tasks[viewIndex].title}".\n\n` +
 `Hang tight for ~2 minutes – the player will update automatically once the final MP4 is ready.`
 );
 return;
 }

 stopVideoPolling();
 const readyUrl = cacheBustUrl(proxiedPlaybackUrl);
 videoStatusRef.current ="ready";
 videoBaseUrlRef.current = playbackBaseUrl;
 setVideoUrl(readyUrl);
 setVideoStatus("ready");
 if (response.usage) {
   setVideoUsage(response.usage);
 }
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 setModalTab("video");
 console.log("[Video Generation] Video ready immediately:", readyUrl);
 } catch (err) {
 console.error('❌ Video generation failed:', err);
 let errorMessage ="Failed to generate video. Please try again.";
 
 // Provide more helpful error messages
 if (err.message) {
 if (err.message.includes("timeout") || err.message.includes("timed out")) {
 errorMessage ="⏱️ Video generation is taking longer than expected. This can happen with complex topics. Please try again in a few minutes.";
 } else if (err.message.includes("service unavailable") || err.message.includes("503")) {
 errorMessage ="🔧 Video generation service is temporarily unavailable. Please try again in a few minutes.";
 } else if (err.message.includes("Missing required") || err.message.includes("400")) {
 errorMessage ="❌ Missing required information. Please ensure the task has a valid topic and all required fields are filled.";
 } else if (err.message.includes("HTML instead of JSON")) {
 errorMessage ="❌ Backend server error. Please contact support if this issue persists.";
 } else {
 errorMessage = `❌ ${err.message}`;
 }
 }
 
 alert(errorMessage);
 videoStatusRef.current ="error";
 setVideoStatus("error");
 setVideoError(errorMessage);
 } finally {
 setVideoGenerating(false);
 }
 };

 const handleVisualize = async () => {
 if (!viewingTask) return;
 setVisualizing(true);
 setVisualUrl("");
 setVisualGeneratedAt(null);
 try {
 console.log('🎨 Generating visualization for:', viewingTask.topic);
 const data = await visualizeOutputAPI({
 userId, bunchName, experienceLevel, task: viewingTask, hint: visualHint
 });
 
 const imageUrl = data.imageUrl || data.visualUrl || data.imageData ||"";
 if (imageUrl) {
 setVisualUrl(imageUrl);
 setVisualGeneratedAt(new Date());
 setModalTab("visualize");
 console.log('✅ Visualization generated successfully');
 } else {
 throw new Error("Visualization service did not return an image URL");
 }
 } catch (e) {
 console.error('❌ Visualization error:', e);
 const errorMsg = e.message ||"Failed to generate visualization";
 alert(`❌ ${errorMsg}\n\nPlease try again. If the issue persists, check your internet connection and OpenAI API configuration.`);
 } finally {
 setVisualizing(false);
 }
 };

 const explainTopic = async () => {
 if (!tasks[viewIndex]) return;
 setExplanationLoading(true);
 try {
 const response = await explainTopicAPI({
 userId,
 roleName: bunchName,
 experienceLevel,
 task: tasks[viewIndex],
 language: explanationLanguage
 });
 // Backend returns: { ok: true, explanation: { explanation: explanationData, ..., usage: {...} } }
 // Extract the nested explanation data
 const explanationData = response.explanation?.explanation || response.explanation;
 // Convert to string immediately to prevent React error #31
 setTopicExplanation(safeToString(explanationData));
 if (response.explanation?.usage) {
   setExplanationUsage(response.explanation.usage);
 }
 } catch (err) {
 console.error('Failed to explain topic:', err);
 alert('Failed to explain topic: ' + err.message);
 } finally {
 setExplanationLoading(false);
 }
 };

 // Alias for voice assistant
 const generateTopicExplanation = explainTopic;

 if (loading) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
 <div className="text-center">
 <ArrowPathIcon className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
 <p className="text-gray-700">Loading your workspace...</p>
 </div>
 </div>
 );
 }

 if (notAllowed) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
 <div className="text-center">
 <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
 <p className="text-gray-700 text-lg">{notAllowed}</p>
 <div className="mt-4 p-4 bg-gray-100 rounded-lg">
 <p className="text-gray-600 text-sm">Debug Info:</p>
 <p className="text-gray-700 text-sm">Current userId: {userId || 'null'}</p>
 <p className="text-gray-700 text-sm">Error: {notAllowed}</p>
 <div className="mt-2 space-y-1">
 <p className="text-gray-600 text-xs">Contact your administrator to be added to a team.</p>
 </div>
 </div>
 <button 
 onClick={() => navigate('/dashboard')}
 className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary transition-colors"
 >
 Go to Dashboard
 </button>
 </div>
 </div>
 );
 }

  return (
   <div className="team-roles-wrapper">
     <div className="min-h-screen bg-white text-gray-900 font-sans relative overflow-x-hidden transition-colors duration-300">
       <div className="flex">
 {/* Main Content */}
 <div className="flex-1 p-4 sm:p-6">
 {/* Header */}
 <div className="bg-gray-100/50 backdrop-blur-sm border border-gray-300 rounded-lg mb-6 transition-colors duration-300">
 <div className="px-6 py-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-4">
 <UserGroupIcon className="h-8 w-8 text-primary" />
 <div>
 <h1 className="text-xl font-bold text-gray-900">
 Team Workspace - {userName}
 </h1>
 <p className="text-sm text-gray-600">
 {experienceLevel} • {bunchName}
 </p>
 <div className="mt-2">
 <SubscriptionStatus />
 </div>
 </div>
 </div>
 
 <div className="flex items-center space-x-4">
 {/* Voice Assistant Button */}
 <button
 onClick={openVoiceAssistant}
 className="flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg text-primary hover:text-primary transition-colors"
 >
 <SpeakerWaveIcon className="h-4 w-4" />
 <span className="text-sm font-medium">Voice Assistant</span>
 </button>

 {/* AI Mentor Button */}
 <button
 onClick={() => {
 if (llmTrialStatus?.hasActiveTrial) {
 setShowMentor(true);
 } else if (llmTrialStatus && llmTrialStatus.daysRemaining <= 0) {
 setShowTrialExpiredModal(true);
 } else {
 setShowTrialModal(true);
 }
 }}
 className="flex items-center gap-2 px-4 py-2 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-600/30 rounded-lg text-blue-400 hover:text-blue-300 transition-colors"
 title={llmTrialStatus?.hasActiveTrial 
 ?"Open AI Mentor for personalized guidance"
 : llmTrialStatus?.daysRemaining <= 0
 ?"Trial expired - Upgrade to Premium"
 : `Trial expires in ${llmTrialStatus?.daysRemaining} days`
 }
 >
 <ChatBubbleLeftRightIcon className="h-4 w-4" />
 <span className="text-sm font-medium">
 AI Mentor {llmTrialStatus?.hasActiveTrial && `(${llmTrialStatus?.daysRemaining}d left)`}
 </span>
 </button>
 
 {/* Theme Toggle */}
 <ThemeToggle />
 
 {/* Progress */}
 <div className="text-right">
 <p className="text-sm text-gray-600">
 {completedTasks}/{totalTasks} tasks completed
 </p>
 <div className="w-32 bg-gray-300 rounded-full h-2">
 <div 
 className="bg-primary h-2 rounded-full transition-all"
 style={{ width: `${progressPercentage}%` }}
 ></div>
 </div>
 </div>

 {/* Team Chat Toggle */}
 {teamId && (
 <button
 onClick={() => setShowTeamChat(!showTeamChat)}
 className="relative p-2 text-muted hover:text-primary transition-colors"
 title="Team Chat"
 >
 <ChatBubbleLeftRightIcon className="h-6 w-6" />
 {teamMembers.length > 0 && (
 <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
 {teamMembers.length}
 </span>
 )}
 </button>
 )}
 </div>
 </div>
 </div>
 </div>


  {/* main */}
  <main className="mx-auto max-w-7xl px-4 py-6">
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar: Task List */}
      <aside className="lg:col-span-1 space-y-4">
        <div className={`p-4 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/40 shadow-sm"}`}>
          <h3 className={`font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Course Roadmap</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
            {tasks.map((t, idx) => {
              const isLocked = idx > unlockedIndex;
              const isCompleted = completedTaskIds.includes(t.id);
              const isActive = viewIndex === idx;
              
              return (
                <button
                  key={t.id}
                  disabled={isLocked}
                  onClick={() => setViewIndex(idx)}
                  className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${
                    isActive 
                      ? "border-primary bg-primary/10" 
                      : isLocked 
                        ? "opacity-50 cursor-not-allowed border-transparent" 
                        : isDark ? "border-accentGray/10 hover:border-accentGray/30" : "border-gray-100 hover:border-primary/30"
                  }`}
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                    isCompleted ? "bg-green-500/10 text-green-500" : isActive ? "bg-primary text-white" : "bg-secondary/10 text-muted"
                  }`}>
                    {isCompleted ? <CheckCircleIconSolid className="h-5 w-5" /> : <span className="text-sm font-bold">{idx + 1}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-bold truncate ${isActive ? "text-primary" : isDark ? "text-white" : "text-gray-900"}`}>{t.title || t.topic}</p>
                    <p className="text-xs text-muted truncate">{isLocked ? "Locked" : isCompleted ? "Completed" : "In Progress"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Main Content: 4 Sections */}
      <div className="lg:col-span-3 space-y-6">
        <div className={`p-6 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/40 shadow-sm"}`}>
          {/* Header & Tabs */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted font-bold">Task {viewIndex + 1} of {totalTasks}</p>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{tasks[viewIndex]?.title || tasks[viewIndex]?.topic}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <TabButton active={modalTab === "overview"} onClick={() => setModalTab("overview")}>Overview</TabButton>
              <TabButton active={modalTab === "resources"} onClick={() => setModalTab("resources")}>AI Resources</TabButton>
              <TabButton active={modalTab === "video"} onClick={() => setModalTab("video")}>Video Tutorial</TabButton>
              <TabButton active={modalTab === "submit"} onClick={() => setModalTab("submit")}>Submit AI Review</TabButton>
              <TabButton active={modalTab === "feedback"} onClick={() => setModalTab("feedback")}>Feedback</TabButton>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {tasks[viewIndex] ? (
              <>
                {modalTab === "overview" && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <Field label="Description">
                          <p className={`text-sm leading-relaxed ${isDark ? "text-gray-300" : "text-gray-600"}`}>{tasks[viewIndex].description}</p>
                        </Field>
                        {tasks[viewIndex].useCase && (
                          <Info label="Use Case">
                            <DocumentTextIcon className="h-4 w-4 text-primary" /> {tasks[viewIndex].useCase}
                          </Info>
                        )}
                      </div>
                      <div className="space-y-4">
                        {tasks[viewIndex].expectedOutput && (
                          <Info label="Expected Output">
                            <CheckCircleIcon className="h-4 w-4 text-primary" /> {tasks[viewIndex].expectedOutput}
                          </Info>
                        )}
                        {tasks[viewIndex].subtopics?.length > 0 && (
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-gray-50"}`}>
                            <p className="text-sm font-bold mb-2">Key Topics:</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {tasks[viewIndex].subtopics.map((s, i) => (
                                <button
                                  key={i}
                                  onClick={() => explainWithAI(s)}
                                  className="px-3 py-1.5 bg-primary/5 hover:bg-primary/20 border border-primary/20 rounded-lg text-sm text-primary font-medium flex items-center gap-2 transition-all group text-left"
                                >
                                  {s}
                                  <ChatBubbleLeftRightIcon className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                                </button>
                              ))}
                            </div>
                            <p className="text-[10px] text-muted mt-3">Click on any subtopic to get a detailed explanation from our AI Assistant.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {modalTab === "resources" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                      <p className="text-muted">Loading AI Resources...</p>
                      <Button variant="contained" className="mt-4" onClick={() => openCurrentTask("resources")}>Generate Resources</Button>
                    </div>
                  </div>
                )}

                {modalTab === "video" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 text-center py-12">
                    <PlayCircleIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                    <p className="text-muted">Video Tutorial will appear here.</p>
                  </div>
                )}

                {modalTab === "submit" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    <div className={`p-6 rounded-2xl border-2 border-dashed ${isDark ? "border-accentGray/20" : "border-gray-200"}`}>
                      <h4 className="font-bold mb-4 flex items-center gap-2">
                        <GitHubIcon className="h-5 w-5" /> GitHub Repository
                      </h4>
                      {githubRepoUrl ? (
                        <div className="space-y-4">
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/10" : "bg-gray-50"} flex items-center justify-between`}>
                            <code className="text-sm text-primary">{githubRepoUrl}</code>
                            <a href={githubRepoUrl} target="_blank" rel="noreferrer" className="text-xs font-bold text-primary hover:underline">Open Repo</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <div className={`h-2 w-2 rounded-full ${invitationStatus === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                            <span className="text-muted">Invitation Status: <span className="font-bold capitalize">{invitationStatus || 'pending'}</span></span>
                          </div>
                          {invitationStatus !== 'active' && (
                            <div className="space-y-3">
                              <p className="text-xs text-muted">Please check your email and accept the GitHub invitation to enable AI reviews.</p>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={syncInvitationStatus}
                                startIcon={<ArrowPathIcon className="h-4 w-4" />}
                              >
                                Check Invitation Status
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted italic">No repository provisioned. Please contact support if you enrolled with a GitHub username.</p>
                      )}
                    </div>
                    
                    <div className="flex justify-end">
                      <Button 
                        variant="contained" 
                        disabled={invitationStatus !== 'active'}
                        onClick={() => openCurrentTask("submit")}
                      >
                        Proceed to Review
                      </Button>
                    </div>
                  </div>
                )}
                
                {modalTab === "feedback" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 text-center py-12">
                    <ChatBubbleLeftRightIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                    <p className="text-muted">No feedback available yet. Submit your task first.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-muted font-medium">Preparing your workspace...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

 {/* CERTIFICATE */}
 <div className="rounded-2xl border border-secondary/40 bg-gray-50 p-5">
 <h3 className="text-base font-semibold flex items-center gap-2 text-gray-900">
 <TrophyIcon className="h-5 w-5 text-primary" /> Certificate
 </h3>
 <p className="mt-1 text-sm text-gray-600">
 {certificateEligible ?"Unlocked! Download your certificate." :"Complete all tasks to unlock your certificate."}
 </p>
 <button
 onClick={() => navigate(`/bootcamp/certificate`)}
 className={`mt-4 w-full rounded-xl px-4 py-3 font-semibold transition border flex items-center justify-center gap-2 ${
 certificateEligible
 ?"bg-primary text-white border-primary hover:bg-primary"
 :"bg-transparent text-primary border-secondary/50 hover:bg-secondary/10"
 }`}
 >
 <TrophyIcon className="h-5 w-5" /> {certificateEligible ?"View / Download Certificate" :"View Requirements"}
 </button>
 <div className="mt-4">
 <ProgressRing percent={progressPercentage} />
 </div>
 </div>

{/* Team Score Widget and Buttons - Side by Side */}
<div className="flex flex-col items-center gap-6 mb-6">
{/* Team Score Widget - Above Calendar, Centered */}
{teamId && (
<div className="w-full max-w-xl">
<TeamScoreWidget teamId={teamId} userId={userId} />
</div>
)}

{/* Ready to Code - Full Width */}
<div className="w-full">
<SimplePracticeCodingButton />
</div>
</div>

 {/* Roadmap */}
 <div className="rounded-2xl border border-secondary/40 bg-gray-50 p-5">
 <div className="flex items-center justify-between mb-3">
 <h3 className="text-base font-semibold flex items-center gap-2 text-gray-900">
 <ClipboardDocumentListIcon className="h-5 w-5 text-primary" /> Personalized AI Roadmap
 </h3>
 {tasks.some(t => t.isPreview) && (
  <button 
    onClick={() => navigate('/skillgenpayments')}
    className="text-[10px] font-bold bg-primary text-white px-2 py-1 rounded hover:bg-primary/90 animate-pulse"
  >
    UPGRADE FOR FULL ACCESS
  </button>
 )}
 </div>
 
 {totalTasks === 0 ? (
 <p className="text-sm text-gray-600">No tasks available for this selection yet.</p>
 ) : (
 <ul className="space-y-2">
 {tasks.map((task, idx) => {
 const isPreview = task.isPreview;
 const completed = completedTaskIds.includes(`${idx}`);
 const unlocked = idx === 0 || completedTaskIds.includes(`${idx - 1}`);
 const current = idx === completedTasks;
 
 return (
 <li key={task.id} className={`flex items-center justify-between p-3 rounded-lg border ${
 isPreview ? 'bg-blue-50/50 border-blue-200' : 'bg-white/50 border-sky-200'
 }`}>
 <div className="flex items-center gap-3">
 <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
 isPreview 
 ? 'bg-blue-500 text-white'
 : completed 
 ? 'bg-green-500 text-white' 
 : current 
 ? 'bg-primary text-white' 
 : unlocked 
 ? 'bg-gray-400 text-gray-700 ' 
 : 'bg-gray-500 text-gray-600 '
 }`}>
 {idx + 1}
 </div>
 <div>
 <h4 className={`text-sm font-medium ${
 isPreview ? 'text-blue-600' : completed ? 'text-green-600' : current ? 'text-primary' : 'text-black'
 }`}>
 {task.title} {isPreview && '(Preview)'}
 </h4>
 <p className="text-xs text-gray-600">
 {task.subtopics?.length || 0} subtopics
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 {isPreview && <ShieldCheckIcon className="h-5 w-5 text-blue-500" />}
 {completed && <CheckCircleIcon className="h-5 w-5 text-green-600" />}
 {current && !isPreview && <PlayCircleIcon className="h-5 w-5 text-primary" />}
 {(!unlocked && !isPreview) && <LockClosedIcon className="h-5 w-5 text-gray-500" />}
 {(unlocked || isPreview) && !completed && (!current || isPreview) && (
 <button
 onClick={() => handleTaskClick(idx)}
 className={`text-xs text-white px-3 py-1 rounded-full font-semibold transition-colors ${
 isPreview ? 'bg-blue-500 hover:bg-blue-600' : 'bg-primary hover:bg-primary'
 }`}
 >
 {isPreview ? 'Preview' : 'Unlocked'}
 </button>
 )}
 </div>
 </li>
 );
 })}
 </ul>
 )}
 </div>
 {/* Team Chat Sidebar */}
 {teamId && (
 <aside className={`fixed right-0 top-0 h-full w-80 bg-[#f0f9ff] border-l border-sky-200 transform transition-transform duration-300 z-50 ${showTeamChat ? 'translate-x-0' : 'translate-x-full'}`}>
 <div className="p-4 border-b border-sky-200">
 <div className="flex items-center justify-between">
 <h3 className="text-lg font-semibold flex items-center gap-2 text-black">
 <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary" /> Team Chat
 </h3>
 <button
 onClick={() => setShowTeamChat(false)}
 className="p-1 rounded-lg text-muted hover:text-white transition-colors"
 >
 <XMarkIcon className="h-5 w-5" />
 </button>
 </div>
 </div>
 
 <div className="flex-1 overflow-y-auto p-4 space-y-4">
 {/* Team Members */}
 <div className="space-y-2">
 <h4 className="text-sm font-medium text-muted">Team Members</h4>
 <div className="space-y-2">
 {teamMembers.map((member, index) => (
 <div key={member.user_id} className="flex items-center gap-2 bg-bgSection/50 rounded-lg px-3 py-2">
 <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-black text-sm font-bold">
 {member.name.charAt(0).toUpperCase()}
 </div>
 <div>
 <p className="text-sm text-black font-medium">{member.name}</p>
 <p className="text-xs text-muted">{member.role || 'Team Member'}</p>
 </div>
 </div>
 ))}
 </div>
 </div>
 
 {/* Chat Messages */}
 <div className="space-y-2">
 <h4 className="text-sm font-medium text-muted">Messages</h4>
 <div className="max-h-64 overflow-y-auto space-y-2">
 {teamChatMessages.map((message) => (
 <div key={message.id} className="flex gap-2">
 <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-black text-xs font-bold">
 {message.user_name?.charAt(0).toUpperCase() || 'U'}
 </div>
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <p className="text-sm text-black font-medium">{message.user_name}</p>
 <span className="text-xs text-gray-500">
 {new Date(message.created_at).toLocaleTimeString()}
 </span>
 </div>
 <p className="text-sm text-muted">{message.message}</p>
 </div>
 </div>
 ))}
 </div>
 </div>
 
 {/* Chat Input */}
 <div className="space-y-2">
 <input
 type="text"
 value={teamChatText}
 onChange={(e) => setTeamChatText(e.target.value)}
 placeholder="Type a message..."
 className="w-full bg-bgSection/50 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-secondary"
 onKeyPress={(e) => e.key === 'Enter' && handleSendTeamMessage()}
 />
 <button
 onClick={handleSendTeamMessage}
 disabled={!teamChatText.trim() || sendingTeamMessage}
 className="w-full bg-primary text-white py-2 rounded-lg text-sm font-semibold hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
 >
 Send Message
 </button>
 </div>
 </div>
 </aside>
 )}

 {/* Task Modal */}
 {showModal && viewIndex !== null && tasks[viewIndex] && (
 <div className="fixed inset-0 bg-bgMain/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
 <div className="bg-bgSection rounded-2xl border border-gray-700 w-full max-w-5xl max-h-[90vh] overflow-hidden">
 {/* Header */}
 <div className="p-6 border-b border-gray-700">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <DocumentTextIcon className="h-6 w-6 text-primary" />
 <h2 className="text-xl font-bold text-primary">Task</h2>
 </div>
 <button
 onClick={() => setShowModal(false)}
 className="p-2 rounded-lg text-muted hover:text-white transition-colors"
 >
 <XMarkIcon className="h-6 w-6" />
 </button>
 </div>
 
 {/* Tabs */}
 <div className="flex gap-1 mt-4">
              {['overview', 'resources', 'video', 'submit', 'feedback'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setModalTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    modalTab === tab
                      ? 'bg-primary text-white border border-white'
                      : 'text-primary border border-primary hover:bg-primary/10'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
 </div>
 
 {/* Content */}
 <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
 {modalTab === 'overview' && (
 <div className="space-y-6">
 <div>
 <h3 className="text-lg font-semibold text-white mb-2">{tasks[viewIndex].title}</h3>
 </div>
 
 <div>
 <h4 className="text-md font-medium text-white mb-2">Description</h4>
 <p className="text-muted">{tasks[viewIndex].description}</p>
 </div>
 
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div className="flex items-start gap-2">
 <DocumentTextIcon className="h-5 w-5 text-primary mt-1" />
 <div>
 <h4 className="text-sm font-medium text-white">Use Case</h4>
 <p className="text-muted text-sm">{tasks[viewIndex].useCase}</p>
 </div>
 </div>
 
 <div className="flex items-start gap-2">
 <CheckCircleIcon className="h-5 w-5 text-primary mt-1" />
 <div>
 <h4 className="text-sm font-medium text-white">Future Use Case</h4>
 <p className="text-muted text-sm">{tasks[viewIndex].futureUseCase}</p>
 </div>
 </div>
 
 <div className="flex items-start gap-2">
 <CheckCircleIcon className="h-5 w-5 text-primary mt-1" />
 <div>
 <h4 className="text-sm font-medium text-white">Expected Output</h4>
 <p className="text-muted text-sm">{tasks[viewIndex].expectedOutput}</p>
 </div>
 </div>
 </div>
 
 <div>
 <h4 className="text-md font-medium text-white mb-2">Subtopics ({tasks[viewIndex].subtopics?.length || 0})</h4>
 <div className="bg-bgSection rounded-lg p-4 max-h-48 overflow-y-auto">
 <div className="flex flex-wrap gap-2">
 {tasks[viewIndex].subtopics?.map((subtopic, idx) => (
   <button
     key={idx}
     onClick={() => explainWithAI(subtopic.subtask_title || subtopic)}
     className="px-3 py-1.5 bg-primary/5 hover:bg-primary/20 border border-primary/20 rounded-lg text-sm text-primary font-medium flex items-center gap-2 transition-all group text-left"
   >
     {subtopic.subtask_title || subtopic}
     <ChatBubbleLeftRightIcon className="h-4 w-4 opacity-50 group-hover:opacity-100" />
   </button>
 ))}
 </div>
 <p className="text-[10px] text-muted mt-3">Click on any subtopic to get a detailed explanation from our AI Assistant.</p>
 </div>
 </div>
 </div>
 )}
 
 {modalTab === 'resources' && (
 <div className="space-y-6">
 <div className="text-center">
 <BookOpenIcon className="h-12 w-12 text-primary mx-auto mb-4" />
 <h3 className="text-lg font-semibold text-white mb-2">Ready to Learn?</h3>
 <p className="text-muted mb-4">
 Generate a comprehensive AI-powered explanation of <strong>{tasks[viewIndex].title}</strong> tailored specifically for your experience level and course.
 </p>

 <button
 onClick={explainTopic}
 disabled={explanationLoading}
 className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary disabled:opacity-50 transition-colors flex items-center gap-2 mx-auto"
 >
 <BookOpenIcon className="h-5 w-5" />
 {explanationLoading ? `Generating in ${explanationLanguage === 'en' ? 'English' : explanationLanguage.toUpperCase()}...` : 'Generate Topic Explanation'}
 </button>
 </div>
 
 {topicExplanation && typeof topicExplanation === 'string' && topicExplanation.trim() && (
 <div className="bg-bgSection rounded-lg p-4">
 <div className="flex items-center justify-between mb-2">
 <h4 className="text-md font-medium text-white">AI Explanation</h4>
 <div className="flex items-center gap-2">
 <select
 value={ttsLanguage}
 onChange={(e) => setTtsLanguage(e.target.value)}
 className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-primary"
 disabled={isSpeaking}
 >
 {ttsLanguages.map((lang) => (
 <option key={lang.code} value={lang.code}>
 {lang.name}
 </option>
 ))}
 </select>
 {isSpeaking ? (
 <button
 onClick={stopSpeaking}
 className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1"
 >
 <XMarkIcon className="h-3 w-3" />
 Stop
 </button>
 ) : (
 <button
 onClick={() => speakText(topicExplanation)}
 className="bg-primary hover:bg-primary text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1"
 title="Listen to explanation"
 >
 <SpeakerWaveIcon className="h-3 w-3" />
 Listen
 </button>
 )}
 </div>
 </div>
 <div className="text-muted whitespace-pre-line text-sm leading-relaxed">
 {topicExplanation}
 </div>
 </div>
 )}
 
 <div className="bg-bgSection rounded-lg p-4">
 <h4 className="text-md font-medium text-white mb-2 flex items-center gap-2">
 <DocumentTextIcon className="h-5 w-5 text-primary" />
 Basic Topic Information
 </h4>
 <div className="space-y-3">
 <div>
 <h5 className="text-sm font-medium text-white">Description</h5>
 <p className="text-muted text-sm">{tasks[viewIndex].description}</p>
 </div>
 <div>
 <h5 className="text-sm font-medium text-white">Use Case</h5>
 <p className="text-muted text-sm">{tasks[viewIndex].useCase}</p>
 </div>
 </div>
 </div>
 </div>
 )}
 
 {modalTab === 'video' && (
 <div className="space-y-6">
 <div className="text-center">
 <div className="flex items-center justify-center gap-2 mb-4">
 <VideoCameraIcon className="h-8 w-8 text-primary" />
 <PhotoIcon className="h-8 w-8 text-primary" />
 </div>
 <h3 className="text-lg font-semibold text-white mb-2">AI Tutorial Video Generator</h3>
 </div>
 
 <div className="bg-bgSection rounded-lg p-4">
 <h4 className="text-md font-medium text-white mb-2 flex items-center gap-2">
 <VideoCameraIcon className="h-5 w-5 text-primary" />
 What You'll Get
 </h4>
 <p className="text-muted mb-4">
 A comprehensive tutorial video for <strong className="text-primary">{tasks[viewIndex].title}</strong>
 </p>
 <ul className="space-y-2 text-primary">
 <li>• Duration: 60-90 seconds</li>
 <li>• {tasks[viewIndex].subtopics?.length || 0} subtopics covered</li>
 <li>• HD Quality (1280x720)</li>
 <li>• Code examples included</li>
 </ul>
 </div>
 
 {!videoUrl && videoStatus !=="queued" ? (
 <div className="space-y-4">
 <div className="text-center">
 <button
 onClick={generateVideo}
 disabled={videoGenerating}
 className="bg-primary text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary disabled:opacity-50 transition-colors flex items-center gap-2 mx-auto"
 >
 {videoGenerating ? (
 <>
 <ArrowPathIcon className="h-5 w-5 animate-spin" />
 <span>Generating Tutorial Video...</span>
 </>
 ) : (
 <>
 <VideoCameraIcon className="h-5 w-5" />
 <span>Generate Tutorial Video</span>
 </>
 )}
 </button>
 </div>
 
 <div className="grid grid-cols-2 gap-4">
 <div className="flex items-center gap-2 text-green-400">
 <CheckCircleIcon className="h-5 w-5" />
 <span className="text-sm">Professional narration</span>
 </div>
 <div className="flex items-center gap-2 text-green-400">
 <CheckCircleIcon className="h-5 w-5" />
 <span className="text-sm">Real-world examples</span>
 </div>
 <div className="flex items-center gap-2 text-green-400">
 <CheckCircleIcon className="h-5 w-5" />
 <span className="text-sm">Visual slides & code</span>
 </div>
 <div className="flex items-center gap-2 text-green-400">
 <CheckCircleIcon className="h-5 w-5" />
 <span className="text-sm">Best practices</span>
 </div>
 </div>
 </div>
 ) : (
 <div className="space-y-4">
 {/* Success Message */}
 <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-2">
 <div className="bg-green-500/20 p-2 rounded-lg">
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 </div>
 <div>
 <h5 className="font-semibold text-green-400">
 {videoStatus ==="ready" ?"🎉 Video Generated Successfully!" :"⏳ Video rendering in progress"}
 </h5>
 <p className="text-sm text-muted">
 {videoStatus ==="ready"
 ?"Your tutorial video is ready to watch"
 : videoMessage ||"We're refreshing the player automatically until the MP4 is ready."}
 </p>
 </div>
 </div>
 </div>
 
 {/* Video Player */}
 <div className="space-y-3">
 <div className="relative bg-bgMain rounded-lg overflow-hidden shadow-lg">
 {videoUrl ? (
 <>
 <video
 controls
 className="w-full rounded-lg"
 style={{ maxHeight: '400px' }}
 poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4MCIgaGVpZ2h0PSI3MjAiIHZpZXdCb3g9IjAgMCAxMjgwIDcyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEyODAiIGhlaWdodD0iNzIwIiBmaWxsPSIjMUYyOTM3Ii8+CjxjaXJjbGUgY3g9IjY0MCIgY3k9IjM2MCIgcj0iNDAiIGZpbGw9IiNGQkJGMjQiLz4KPHBhdGggZD0iTTYyMCAzNDBMMjYwIDM0MEwyNjAgMzgwTDYyMCAzODBMNjIwIDM0MFoiIGZpbGw9IiMxRjI5MzciLz4KPC9zdmc+"
 onCanPlay={handleVideoLoaded}
 onLoadedData={handleVideoLoaded}
 onError={handleVideoLoadError}
 >
 <source src={getProxiedVideoUrl(videoUrl)} type="video/mp4" />
 Your browser does not support the video tag.
 </video>
 {videoStatus !=="ready" && (
 <div className="absolute inset-0 bg-bgMain/70 flex flex-col items-center justify-center text-center p-4">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mb-3" />
 <p className="text-sm text-gray-200">
 {videoMessage ||"Waiting for the video to finish rendering…"}
 </p>
 </div>
 )}
 </>
 ) : (
 <div className="p-6 text-center">
 {videoStatus ==="queued" || videoStatus ==="generating" ? (
 <div className="flex flex-col items-center justify-center gap-3">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin" />
 <p className="text-sm text-gray-200 font-medium">
 {videoMessage ||"Rendering video… This usually takes 1-3 minutes."}
 </p>
 <p className="text-xs text-muted">
 We'll update automatically once the video is ready.
 </p>
 </div>
 ) : (
 <div className="text-sm text-muted">
 Video URL not available yet. Generate a video to get started.
 </div>
 )}
 </div>
 )}
 </div>
 {videoError && (
 <div className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-lg p-3">
 {videoError}
 </div>
 )}
 
 {/* Action Buttons */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <button
 onClick={() => window.open(videoUrl, '_blank')}
 disabled={videoStatus !=="ready"}
 className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
 title={videoStatus ==="ready" ?"Open video in a new tab" :"Video still rendering"}
 >
 <ArrowTopRightOnSquareIcon className="h-5 w-5" />
 Open in New Tab
 </button>
 <button
 onClick={async () => {
 try {
 const response = await fetch(videoUrl);
 const blob = await response.blob();
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 link.download = `tutorial-${tasks[viewIndex]?.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'video'}.mp4`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(url);
 } catch (err) {
 console.error('Download failed:', err);
 alert('Download failed. Please try opening in a new tab instead.');
 }
 }}
 disabled={videoStatus !=="ready"}
 className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
 title={videoStatus ==="ready" ?"Download MP4" :"Video still rendering"}
 >
 <ArrowDownTrayIcon className="h-5 w-5" />
 Download Video
 </button>
 </div>
 
 {/* Regenerate Button */}
 <button
 onClick={generateVideo}
 className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
 >
 <ArrowPathIcon className="h-4 w-4" />
 Regenerate Video
 </button>
 </div>
 </div>
 )}
 </div>
 )}
 
 {modalTab === 'submit' && (
      <div className="space-y-6">
        {/* Provisioned Repo Notice */}
        {githubRepoUrl && (
          <div className="rounded-xl border border-primary/40 bg-primary/10 p-4 mb-4">
            <h4 className="text-sm font-semibold text-primary mb-1 flex items-center gap-2">
              <CheckCircleIcon className="h-4 w-4" />
              Your Dedicated Project Repository
            </h4>
            <p className="text-xs text-muted mb-2">
              We've created a private GitHub repository for your project. Please push your code here.
            </p>
            <div className="flex items-center gap-3">
              <code className="bg-bgMain/60 px-2 py-1 rounded text-xs text-primary border border-primary/20 flex-1 truncate">
                {githubRepoUrl}
              </code>
              <a 
                href={githubRepoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs bg-primary text-white px-3 py-1 rounded hover:bg-secondary transition"
              >
                Open Repo
              </a>
            </div>
            {invitationStatus === 'SENT' && (
              <p className="text-[10px] text-orange-400 mt-2 italic">
                ⚠️ Check your email to accept the GitHub invitation if you haven't yet.
              </p>
            )}
          </div>
        )}

        {/* Submission Type Selection (Only if no provisioned repo) */}
        {!githubRepoUrl && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircleIcon className="h-6 w-6 text-primary" />
              <h3 className="text-lg font-semibold text-white">Choose Submission Type</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button onClick={()=>setSubmissionType("github")} className={`bg-bgSection rounded-lg p-4 border-2 transition-all text-left ${submissionType==="github"?"border-primary":"border-gray-600 hover:border-primary"}`}>
                <div className="w-8 h-8 bg-gray-600 rounded mb-2 flex items-center justify-center">
                  <span className="text-white text-xs">GH</span>
                </div>
                <h4 className="text-primary font-semibold">GitHub Repository</h4>
                <p className="text-muted text-sm">Submit your code repository</p>
              </button>
              <button onClick={()=>setSubmissionType("files")} className={`bg-bgSection rounded-lg p-4 border-2 transition-all text-left ${submissionType==="files"?"border-primary":"border-gray-600 hover:border-primary"}`}>
                <DocumentTextIcon className="h-8 w-8 text-primary mb-2" />
                <h4 className="text-primary font-semibold">Document + Image</h4>
                <p className="text-muted text-sm">Upload documentation and screenshots</p>
              </button>
            </div>
          </>
        )}

        {submissionType === "github" && (
          <div>
            <p className="text-sm text-muted mb-3">
              {githubRepoUrl 
                ? "We'll review the code in your dedicated repository." 
                : "Provide your GitHub repository URL for code review."
              }
            </p>
            {!githubRepoUrl && (
              <>
                <label className="block text-sm text-muted mb-1">GitHub Repository URL</label>
                <input type="url" className="w-full bg-bgSection border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-secondary" placeholder="https://github.com/username/repository" value={githubUrl} onChange={(e)=>setGithubUrl(e.target.value)} disabled={submittingReview}/>
              </>
            )}
          </div>
        )}

        {!githubRepoUrl && submissionType === "files" && (
          <div>
 <h4 className="text-md font-medium text-white mb-2">
 Upload your <strong className="text-primary">document</strong> and an <strong className="text-primary">image</strong>
 </h4>
 <div className="space-y-4">
 <div>
 <label className="block text-sm text-muted mb-1">
 Document (required)
 <span className="text-xs text-muted ml-2">
 PDF, DOC, DOCX, MD, TXT, PY, JS, JAVA, CPP, SQL, JSON, XML, YAML
 </span>
 </label>
 <div className="flex items-center gap-2">
 <input 
 type="file" 
 accept=".pdf,.doc,.docx,.md,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown"
 onChange={(e) => {
 const file = e.target.files?.[0];
 if (file) {
 setDocFile(file);
 console.log('Document selected:', file.name);
 }
 }}
 className="hidden"
 id={`document-upload-${viewIndex}`}
 />
 <button 
 onClick={() => document.getElementById(`document-upload-${viewIndex}`).click()}
 className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary transition-colors"
 >
 Choose File
 </button>
 <span className="text-muted text-sm">
 {docFile ? docFile.name :"No file chosen"}
 </span>
 </div>
 </div>
 <div>
 <label className="block text-sm text-muted mb-1">
 Image (required)
 <span className="text-xs text-muted ml-2">
 JPG, JPEG, PNG, GIF, BMP, WEBP
 </span>
 </label>
 <div className="flex items-center gap-2">
 <input 
 type="file" 
 accept="image/*"
 onChange={(e) => {
 const file = e.target.files?.[0];
 if (file) {
 setImgFile(file);
 console.log('Image selected:', file.name);
 }
 }}
 className="hidden"
 id={`image-upload-${viewIndex}`}
 />
 <button 
 onClick={() => document.getElementById(`image-upload-${viewIndex}`).click()}
 className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary transition-colors"
 >
 Choose File
 </button>
 <span className="text-muted text-sm">
 {imgFile ? imgFile.name :"No file chosen"}
 </span>
 </div>
 </div>
 </div>
 </div>
 )}

 <div>
 <label className="block text-sm text-muted mb-2">
 Detailed explanation (required - minimum 50 characters)
 <span className="text-xs text-muted ml-2">
 {comments ? `${comments.length}/50` : '0/50'}
 </span>
 </label>
 <textarea
 placeholder="Explain your approach, implementation details, challenges faced, and how your solution addresses the task requirements..."
 className="w-full bg-bgSection border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-secondary h-24"
 value={comments}
 onChange={(e) => setComments(e.target.value)}
 />
 {comments && comments.length < 50 && (
 <p className="text-xs text-red-400 mt-1">
 Please provide at least 50 characters of explanation
 </p>
 )}
 </div>

 <button
 onClick={handleTaskSubmission}
 className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
 disabled={submittingReview || (submissionType==="files" && (!docFile || !imgFile)) || (submissionType==="github" && !githubUrl) || !comments || comments.length < 50}
 >
 {submittingReview ? (
 <>
 <ArrowPathIcon className="h-4 w-4 animate-spin" />
 Submitting...
 </>
 ) : (
"Submit for AI review"
 )}
 </button>
 {((submissionType==="files" && (!docFile || !imgFile)) || (submissionType==="github" && !githubUrl) || !comments || comments.length < 50) && !submittingReview && (
 <p className="text-xs text-primary mt-2">
 ⚠️ Submit button disabled: {submissionType==="files" ? "Please upload both files and" : "Please provide GitHub URL and"} provide detailed explanation (50+ characters)
 </p>
 )}
 {reviewError && (
 <p className="text-xs text-red-400 mt-2">
 {reviewError}
 </p>
 )}
 </div>
 )}
 
 {modalTab === 'feedback' && (
 <div className="space-y-4">
 {tasks[viewIndex] && reviewStateById[tasks[viewIndex].id] ? (
 <div className="bg-bgSection rounded-lg p-6 border border-gray-600">
 <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
 <CheckCircleIcon className="h-5 w-5 text-primary" />
 AI Review Summary
 </h3>
 <div className="space-y-3 text-sm text-gray-200">
 <div className="flex items-center justify-between">
 <span>Status</span>
 <span className="font-semibold text-primary">
 {reviewStateById[tasks[viewIndex].id].status === 'passed' ? 'Approved' :
 reviewStateById[tasks[viewIndex].id].status === 'failed' ? 'Needs Revision' :
 reviewStateById[tasks[viewIndex].id].status === 'pending' ? 'Under Review' : '—'}
 </span>
 </div>
 {typeof reviewStateById[tasks[viewIndex].id].score === 'number' && (
 <div className="flex items-center justify-between">
 <span>Score</span>
 <span className="font-semibold">
 {(reviewStateById[tasks[viewIndex].id].score * 100).toFixed(0)}%
 </span>
 </div>
 )}
 </div>
 {reviewStateById[tasks[viewIndex].id].feedback && (
 <div className="mt-4">
 <h4 className="text-sm font-medium text-muted mb-2">Feedback</h4>
 <p className="text-sm text-gray-200 whitespace-pre-line">
 {reviewStateById[tasks[viewIndex].id].feedback}
 </p>
 </div>
 )}
 </div>
 ) : (
 <div className="text-center py-8">
 <p className="text-muted">No submission yet. Use the"Submit" tab.</p>
 </div>
 )}
 </div>
 )}
 
 {modalTab === 'visualize' && (
 <div className="space-y-6">
 <div>
 <h3 className="text-lg font-semibold text-white mb-2">AI-Powered Visualization Generator</h3>
 <p className="text-muted">
 Generate task-specific visualizations including architecture diagrams, dashboards, flowcharts, and mockups to help you understand how real-world implementations look.
 </p>
 </div>
 
 <div>
 <h4 className="text-md font-medium text-white mb-4">Visualization Types</h4>
 <div className="grid grid-cols-2 gap-4">
 <button 
 className="bg-bgSection rounded-lg p-4 border border-gray-600 hover:border-primary transition-colors text-left"
 onClick={() => setVisualHint(`${visualHint} Architecture Diagram: `)}
 >
 <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
 <h5 className="text-white font-medium">Architecture Diagram</h5>
 </button>
 <button 
 className="bg-bgSection rounded-lg p-4 border border-gray-600 hover:border-primary transition-colors text-left"
 onClick={() => setVisualHint(`${visualHint} Dashboard Mockup: `)}
 >
 <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
 <h5 className="text-white font-medium">Dashboard Mockup</h5>
 </button>
 <button 
 className="bg-bgSection rounded-lg p-4 border border-gray-600 hover:border-primary transition-colors text-left"
 onClick={() => setVisualHint(`${visualHint} Process Flow: `)}
 >
 <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
 <h5 className="text-white font-medium">Process Flow</h5>
 </button>
 <button 
 className="bg-bgSection rounded-lg p-4 border border-gray-600 hover:border-primary transition-colors text-left"
 onClick={() => setVisualHint(`${visualHint} UI/UX Design: `)}
 >
 <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
 <h5 className="text-white font-medium">UI/UX Design</h5>
 </button>
 </div>
 </div>
 
 <div className="bg-bgSection rounded-lg p-4">
 <h4 className="text-md font-medium text-white mb-2">Task Context (Auto-filled for better results)</h4>
 <div className="space-y-2 text-sm">
 <p><strong className="text-primary">Project:</strong> {tasks[viewIndex].projectName}</p>
 <p><strong className="text-primary">Use Case:</strong> {tasks[viewIndex].useCase}</p>
 <p><strong className="text-primary">Expected Output:</strong> {tasks[viewIndex].expectedOutput}</p>
 </div>
 </div>
 
 <div>
 <label className="block text-sm text-muted mb-2">Custom Visualization Prompt</label>
 <textarea
 value={visualHint}
 onChange={(e) => setVisualHint(e.target.value)}
 placeholder="Describe what you'd like to visualize..."
 className="w-full bg-bgSection border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-secondary h-24"
 />
 </div>
 
 <div className="flex justify-center">
 <button
 onClick={handleVisualize}
 disabled={visualizing}
 className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
 >
 {visualizing ? (
 <>
 <ArrowPathIcon className="h-4 w-4 animate-spin" />
 Generating...
 </>
 ) : (
 <>
 <PhotoIcon className="h-4 w-4" />
 Generate Visualization
 </>
 )}
 </button>
 </div>
 
 <VisualizationPreview
 loading={visualizing}
 imageUrl={visualUrl}
 taskTitle={viewingTask?.title || viewingTask?.topic}
 hint={visualHint}
 generatedAt={visualGeneratedAt}
 downloadFileName={`${slugify(viewingTask?.title || viewingTask?.topic || 'visual')}-visual.png`}
 />
 </div>
            )}
          </div>
      </div>
      </div>
            )}
    </main>
   </div>
  </div>
 </div>
</div>
);
}

function MentorModal({ onClose, messages, setMessages, onSend, technology, task }) {
  const [text, setText] = useState("");
  const [imgFile, setImgFile] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sending, setSending] = useState(false);
  const [quickQuestions] = useState([
    `Explain ${technology} in simple terms`,
    `What are the best practices for ${technology}?`,
    `Show me a ${technology} code example`,
    "What are common mistakes to avoid?",
    "How does this relate to real-world projects?"
  ]);
  const listRef = useRef(null);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t && !imgFile) return;
    setSending(true);
    setText("");
    const f = imgFile;
    setImgFile(null);
    await onSend({ text: t, imgFile: f });
    setSending(false);
  };

  const sendQuickQuestion = async (question) => {
    setSending(true);
    await onSend({ text: question, imgFile: null });
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className={`w-full bg-[#f0f9ff] border border-sky-200 rounded-2xl shadow-2xl overflow-hidden ${isFullscreen ? 'h-full max-w-none' : 'max-w-4xl'}`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-sky-100">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-black" />
            <h3 className="font-bold text-black">AI Mentor • {technology}</h3>
          </div>
 <div className="flex items-center gap-2">
 <button
 onClick={() => setIsFullscreen(!isFullscreen)}
 className="text-black hover:text-primary p-1 rounded hover:bg-bgSection"
 title={isFullscreen ?"Exit Fullscreen" :"Enter Fullscreen"}
 >
 {isFullscreen ? (
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
 </svg>
 ) : (
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
 </svg>
 )}
 </button>
 <button className="p-1 rounded hover:bg-zinc-800" onClick={onClose}><XMarkIcon className="h-5 w-5 text-black"/></button>
 </div>
 </div>
 <div className={`grid grid-rows-[1fr_auto] ${isFullscreen ? 'h-[calc(100vh-80px)]' : 'h-[72vh]'}`}>
 <div ref={listRef} className="px-5 py-4 overflow-y-auto">
 {messages.length === 0 && (
 <div className="text-sm text-black">
 Share a screenshot and describe where you're stuck. Guidance is scoped to <span className="text-primary">{technology}</span>
 {task?.title ? <> —"{task.title}"</> : null}.
 </div>
 )}
 <ul className="space-y-3">
 {messages.map((m)=>(
 <li key={m.id} className={`flex ${m.role==="user" ?"justify-end" :"justify-start"}`}>
 <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-line ${m.role==="user" ?"bg-primary text-white" :"bg-white border border-sky-200 text-black shadow-sm"}`}>
 {m.text}
 {m.imagePreview && <img src={m.imagePreview} alt="" className="mt-2 rounded-lg max-h-56" />}
 </div>
 </li>
 ))}
 </ul>
 </div>
 <div className="border-t border-sky-100 p-3">
 <div className="flex items-end gap-2">
 <div className="flex-1">
 <textarea rows={2} className="w-full rounded-lg bg-white border border-sky-200 text-sm text-black p-3 focus:outline-none focus:border-primary" placeholder={`Describe the issue… (${technology})`} value={text} onChange={(e)=>setText(e.target.value)}/>
 <div className="flex items-center justify-between mt-1">
 <label className="text-xs text-black flex items-center gap-2 cursor-pointer">
 <PaperClipIcon className="h-4 w-4 text-black" /> {imgFile ? <span className="text-primary truncate max-w-[12rem]">{imgFile.name}</span> : <span className="text-black">Attach screenshot</span>}
 <input type="file" accept="image/*" className="hidden" onChange={(e)=>setImgFile(e.target.files?.[0] || null)} />
 </label>
 <div className="text-[11px] text-black">Attach an image for pinpoint help.</div>
 </div>
 </div>
 <button onClick={send} className="rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary flex items-center gap-2"><PaperAirplaneIcon className="h-4 w-4" />Send</button>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}



